// ============================================
// Story Content Renderer
// Handles story text, dialogue, images, videos
// with fade-in animations and auto-scroll
// ============================================

import { events, Events } from '../engine/events.js';
import { IMAGE_FOLDERS, SPEAKER_DISPLAY_NAMES } from '../engine/constants.js';
import { appearanceState } from '../systems/appearance.js';
import { storyManager } from '../engine/story.js';
import { L } from '../systems/localization.js';
import { imageVoteService } from '../services/analytics.js';
import { audioManager } from '../systems/audio.js';
import { HYPNO_VOICE_CLIPS, isVoiceClip, createIsolatedClip, releaseClipsIn } from './components/isolated-video.js';

/** Location knot → CSS area class mapping (for border accent) */
const LOCATION_AREA_MAP = {
    'Bar': 'bar', 'BarEntry': 'bar',
    'Park': 'park',
    'Club': 'club', 'ClubEntry': 'club',
    'Mall': 'mall', 'MallEntry': 'mall',
    'Beach': 'beach', 'BeachEntry': 'beach',
    'Gym': 'gym', 'GymEntry': 'gym',
    'Clinic': 'clinic', 'ClinicEntry': 'clinic',
    'Salon': 'salon',
    'Office': 'office',
    'Spa': 'spa', 'SpaEntry': 'spa', 'SpaHub': 'spa',
};

/** Location display names + accent colors */
const LOCATION_STYLES = {
    'Apartment': { name: 'Apartment', color: '#a080e0' },
    'Home':      { name: 'Apartment', color: '#a080e0' },
    'Outside':   { name: 'Outside',   color: '#5cd89c' },
    'Bar':       { name: 'Bar',       color: '#e8c860' },
    'BarEntry':  { name: 'Bar',       color: '#e8c860' },
    'Park':      { name: 'Park',      color: '#5cd89c' },
    'Mall':      { name: 'Mall',      color: '#e88cb8' },
    'MallEntry': { name: 'Mall',      color: '#e88cb8' },
    'Salon':     { name: 'Salon',     color: '#e88cb8' },
    'Beach':     { name: 'Beach',     color: '#5bc0de' },
    'BeachEntry':{ name: 'Beach',     color: '#5bc0de' },
    'Club':      { name: 'Club',      color: '#c470e0' },
    'ClubEntry': { name: 'Club',      color: '#c470e0' },
    'Gym':       { name: 'Gym',       color: '#7090c0' },
    'GymEntry':  { name: 'Gym',       color: '#7090c0' },
    'Clinic':    { name: 'Clinic',    color: '#60c0a0' },
    'ClinicEntry':{ name: 'Clinic',   color: '#60c0a0' },
    'Spa':        { name: 'Serenity Spa', color: '#b478a0' },
    'SpaEntry':   { name: 'Serenity Spa', color: '#b478a0' },
    'SpaHub':     { name: 'Serenity Spa', color: '#b478a0' },
    'Downtown':      { name: 'Downtown',     color: '#e0a060' },
    'CornerStore':   { name: 'Corner Store', color: '#80c070' },
    'CornerStoreEntry': { name: 'Corner Store', color: '#80c070' },
    'RedLight':      { name: 'Red Light District', color: '#e05080' },
    'RedLightEntry': { name: 'Red Light District', color: '#e05080' },
    'Brothel':       { name: 'Velvet Lounge',     color: '#c04070' },
    'BrothelEntry':  { name: 'Velvet Lounge',     color: '#c04070' },
    'MaidCafe':      { name: 'Maid Cafe',         color: '#e8a0c0' },
    'MaidCafeEntry': { name: 'Maid Cafe',         color: '#e8a0c0' },
};

/** Image path resolution cache: imageName → folder */
const imageCache = {};

/** Image manifest: imageName → folder (loaded from JSON, eliminates 404 chains) */
let imageManifest = null;

import { isTarMode, getImageUrl, sweepEvictedBlobs, getImageDataUri, isImgCompatMode, setImgCompatMode } from '../image-loader.js';
import { fetchWithEdgeRetry } from '../engine/story-fetch.js';

// Render-fail breadcrumb (warn-level so VPS bug-report console captures see it;
// capped so a fully broken device can't flush the 15-entry ring with duplicates).
let _renderFailWarned = 0;
function _warnRenderFail(kind, name, src) {
    if (_renderFailWarned >= 5) return;
    _renderFailWarned++;
    const scheme = (src || '').split(':')[0];
    console.warn(`[StoryContent] MEDIA_RENDER_FAIL kind=${kind} name=${name} scheme=${scheme} (element error event — asset resolved but did not render)`);
}

// ─── Broken-device-storage compat plumbing (2026-07-11 Android reports) ────
// Distinct scene images that failed to render this session. At >= 2 distinct
// failures (and compat mode off) offer the memory-only image mode via a
// one-time banner — the class where EVERY blob render fails on a device with
// a broken storage backend. Counters exposed for bug-report game_state
// (image-loader getTarStatus reads window.__pnoRenderStats).
const _renderFailNames = new Set();
if (typeof window !== 'undefined') {
    window.__pnoRenderStats = { fails: 0, retryOk: 0 };
}
function _noteRenderFail(name) {
    _renderFailNames.add(name);
    try { window.__pnoRenderStats.fails = _renderFailNames.size; } catch (e) { /* no window */ }
    if (_renderFailNames.size >= 2) _maybeShowCompatBanner();
}
function _noteRetryOk() {
    try { window.__pnoRenderStats.retryOk++; } catch (e) { /* no window */ }
}

let _compatBannerShown = false;
function _maybeShowCompatBanner() {
    if (_compatBannerShown || isImgCompatMode()) return;
    if (typeof document === 'undefined' || !document.body) return;
    _compatBannerShown = true;
    const t = (k, fb) => { const v = L.get(k); return v !== k ? v : fb; };
    const bar = document.createElement('div');
    bar.id = 'img-compat-banner';
    bar.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:calc(16px + env(safe-area-inset-bottom, 0px));z-index:10050;'
        + 'background:#2a1a2e;color:#f0d8e8;border:1px solid #e8a0c0;border-radius:10px;'
        + 'padding:10px 14px;max-width:min(480px,92vw);font-size:14px;box-shadow:0 4px 18px rgba(0,0,0,.5);';
    const msg = document.createElement('div');
    msg.textContent = t('imgcompat.banner', 'Images are failing to display on this device. A compatibility mode can render them a different way.');
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:8px;margin-top:8px;justify-content:flex-end;';
    const later = document.createElement('button');
    later.textContent = t('imgcompat.dismiss', 'Not now');
    later.style.cssText = 'background:transparent;color:#f0d8e8;border:1px solid #715a6b;border-radius:8px;padding:6px 12px;cursor:pointer;';
    later.onclick = () => bar.remove();
    const enable = document.createElement('button');
    enable.textContent = t('imgcompat.enable', 'Enable compatibility mode');
    enable.style.cssText = 'background:#e8a0c0;color:#201018;border:none;border-radius:8px;padding:6px 12px;cursor:pointer;';
    enable.onclick = () => {
        setImgCompatMode(true);
        bar.remove();
        location.reload();
    };
    row.appendChild(later);
    row.appendChild(enable);
    bar.appendChild(msg);
    bar.appendChild(row);
    document.body.appendChild(bar);
}


/** Set of image names known to be missing (avoids re-trying 404s) */
const imageMissCache = new Set();

// Restore imageCache from localStorage. Older sessions could write a stale
// "Apartment" folder for images that actually live elsewhere (Computer,
// Prologue, Resort, …) due to the IMAGE_FOLDERS[0]-default-arg bug in the
// img.onload caching path. Bump the cache key so existing wrong entries
// are abandoned cleanly on first load after the fix.
const IMAGE_CACHE_KEY = 'PnO_ImageCache_v2';
try {
    const saved = localStorage.getItem(IMAGE_CACHE_KEY);
    if (saved) Object.assign(imageCache, JSON.parse(saved));
    // Drop the legacy key so the next save doesn't keep paying for it.
    localStorage.removeItem('PnO_ImageCache');
} catch(e) {}

// Debounced localStorage save for imageCache (avoid sync I/O on every image load)
// LRU eviction: cap at 500 entries to prevent localStorage bloat
const MAX_IMAGE_CACHE = 500;
let _imageCacheSaveTimer = null;

// HYPNO_VOICE_CLIPS moved to components/isolated-video.js (§ 106) so the
// gallery viewer + the expand modal share the same "voice clips stay in the
// main document" rule. Imported above; all uses below are unchanged.

// Registry of EVERY hypno-voice <video> ever created this session. On any scene
// leave we nuke each one that is no longer the current beat — including ORPHANS
// that were detached from #story-content and so are invisible to
// querySelectorAll AND to audioManager._hypnoVideoEl (which only ever points at
// the single active clip). This is the safety net for the recurring "hypno audio
// won't stop, but it's in no log" report (foundby:owner 0.2.8.6 Firefox): a
// detached `loop` <video> whose pause() Firefox silently ignored keeps emitting
// native audio with no surface left to stop it. Full unload (src removed +
// load()) kills it regardless of the paused flag.
const _hypnoVideoRegistry = new Set();
function _nukeHypnoOrphans() {
    let curLoc = '';
    try { curLoc = (storyManager && storyManager.currentLocation) || ''; } catch (e) {}
    for (const v of [..._hypnoVideoRegistry]) {
        try {
            // Keep the one clip that belongs to the CURRENT beat (active scene).
            const beat = v.dataset ? v.dataset.clipLoc : undefined;
            const isCurrent = v.isConnected && beat !== undefined && beat === curLoc;
            if (isCurrent) continue;
            v.muted = true;
            v.loop = false;
            v.pause();
            v.removeAttribute('src');
            v.load();          // mechanism-independent: stops native audio even if pause() is ignored (FF152)
            _hypnoVideoRegistry.delete(v);
        } catch (e) { _hypnoVideoRegistry.delete(v); }
    }
}
function _debounceSaveImageCache() {
    if (_imageCacheSaveTimer) return;
    _imageCacheSaveTimer = setTimeout(() => {
        _imageCacheSaveTimer = null;
        try {
            const keys = Object.keys(imageCache);
            if (keys.length > MAX_IMAGE_CACHE) {
                const excess = keys.slice(0, keys.length - MAX_IMAGE_CACHE);
                for (const k of excess) delete imageCache[k];
            }
            localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(imageCache));
        } catch(e) {}
    }, 2000);
}

/**
 * Load image folder manifest (maps image names to their folder).
 * Call during init to eliminate 404 fallback chains.
 */
async function loadImageManifest() {
    try {
        // Edge-403-resilient (itch.io BunnyCDN) — a transient 403 here drops
        // the player into the 404 fallback chain for the whole session.
        const resp = await fetchWithEdgeRetry('assets/data/image_manifest.json');
        imageManifest = await resp.json();
        window._imageManifest = imageManifest; // Expose for locations.js + other modules
        // Register all manifest image names with AppearanceState for variant resolution
        appearanceState.registerKnownImages(Object.keys(imageManifest));
        console.log(`[PnO] Image manifest loaded: ${Object.keys(imageManifest).length} entries`);
    } catch (e) {
        console.warn('[PnO] Image manifest not available, using fallback chain');
    }
}

class StoryContent {
    constructor() {
        /** @type {HTMLElement|null} */
        this._contentEl = null;
        // § 91 catch-all: true once a #gif video was (re)rendered during the
        // current refresh. Reset on STORY_REFRESH_START, set in _addVideo, read
        // on STORY_CHOICES_READY — if a refresh presents choices WITHOUT a fresh
        // video, the player left the active hypno clip and any still-unmuted
        // inline video must be silenced (covers same-location / zero-narration
        // / computer-power-off leaves that fire none of the other handlers).
        this._videoAddedThisRefresh = false;
        // Set when the location/scene changed this refresh; consumed by the
        // end-of-refresh STORY_CHOICES_READY sweep (_stopAllMedia) so a clip the
        // panel-recovery re-creates AFTER the mid-refresh silence still gets stopped.
        this._sceneChangedThisRefresh = false;
        /** @type {HTMLElement|null} */
        this._storyEl = null;
        /** @type {HTMLElement|null} */
        this._typingEl = null;
        /** @type {string|null} Current speaker from #char: tag */
        this._currentSpeaker = null;
        /** @type {string|null} Last portrait used in dialogue */
        this._lastPortrait = null;
        /** @type {string[]} History of story text for log */
        this.history = [];
        /** @type {number|null} Active typing animation timer */
        this._typingTimer = null;
        /** @type {boolean} Whether to skip current typing animation */
        this._skipTyping = false;
        /** @type {boolean} True while a card is being typed out char-by-char */
        this.isTyping = false;
        /** @type {boolean} */
        this._bound = false;
        /** @type {HTMLElement|null} Scroll anchor for append mode (separator element) */
        this._scrollAnchor = null;
        /** @type {IntersectionObserver|null} Pauses off-screen silent scrollback #gif loops */
        this._videoVisibilityObserver = null;
    }

    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    init() {
        if (this._bound) return;
        this._bound = true;

        this._contentEl = document.getElementById('story-content');
        this._storyEl = document.getElementById('story');
        this._typingEl = document.getElementById('typing-indicator');

        this._scope = events.createScope('story-content');
        this._bindEvents();
        this._bindImageModal();
        this._bindScrollIndicator();
        this._bindSkipInputs();
        this._initLazyObserver();
        this._initVideoVisibilityObserver();

        // Tar self-heal (image-loader background retry): re-resolve the
        // placeholder cards _addImage parked while the archive was incomplete.
        /** @type {Array<{name: string, wrapper: HTMLElement}>} */
        this._pendingHealImages = [];
        this._onTarHealedHandler = () => this._onTarHealed();
        window.addEventListener('pno-tar-healed', this._onTarHealedHandler);

        // Load image manifest (async, non-blocking — fallback chain works until loaded)
        loadImageManifest();
    }

    /** Detach all event listeners + observers (tests + future re-init paths). */
    destroy() {
        if (this._lazyObserver) {
            this._lazyObserver.disconnect();
            this._lazyObserver = null;
        }
        if (this._videoVisibilityObserver) {
            this._videoVisibilityObserver.disconnect();
            this._videoVisibilityObserver = null;
        }
        if (this._skipKeyHandler) {
            // capture: true must match addEventListener call in _bindSkipInputs.
            document.removeEventListener('keydown', this._skipKeyHandler, true);
            this._skipKeyHandler = null;
        }
        if (this._skipWheelHandler) {
            const storyScrollEl = document.getElementById('story') || this._storyEl;
            if (storyScrollEl) storyScrollEl.removeEventListener('wheel', this._skipWheelHandler);
            this._skipWheelHandler = null;
        }
        if (this._onTarHealedHandler) {
            window.removeEventListener('pno-tar-healed', this._onTarHealedHandler);
            this._onTarHealedHandler = null;
        }
        if (this._imageModalKeyHandler) {
            document.removeEventListener('keydown', this._imageModalKeyHandler);
            this._imageModalKeyHandler = null;
        }
        if (this._modalVisibilityHandler) {
            document.removeEventListener('visibilitychange', this._modalVisibilityHandler);
            this._modalVisibilityHandler = null;
        }
        if (this._scrollIndicatorHandler && this._scrollIndicatorEl) {
            this._scrollIndicatorEl.removeEventListener('scroll', this._scrollIndicatorHandler);
            this._scrollIndicatorHandler = null;
            this._scrollIndicatorEl = null;
        }
        this._scope.destroy();
    }

    /**
     * IntersectionObserver for lazy-loading images.
     * Images get data-src instead of src; observer sets src when 200px from viewport.
     */
    _initLazyObserver() {
        if (typeof IntersectionObserver === 'undefined') return;
        this._lazyObserver = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    const img = entry.target;
                    const lazySrc = img.dataset.lazySrc;
                    if (lazySrc) {
                        img.src = lazySrc;
                        delete img.dataset.lazySrc;
                    }
                    this._lazyObserver.unobserve(img);
                }
            }
        }, {
            root: this._storyEl,
            rootMargin: '200px 0px'  // Start loading 200px before visible
        });
    }

    // Pause the lingering SILENT scrollback #gif once it scrolls out of view;
    // resume it when it scrolls back in. A new #gif REMOVES the previous
    // .story-video (see _addVideo), so at most ONE silent clip persists, it sits
    // in the scrollback looping a 1024² decode the whole time the player reads
    // text beats below it: pure GPU/CPU cost for motion nobody is watching
    // ("animations run very slowly", 0.2.9). Pausing it off-screen kills the
    // decode; scrolling back UP re-enters the viewport and el._pnoResumeIfVisible
    // (a guarded _tryPlay) restarts the loop, so "scroll up to see it animate"
    // still works. AUDIO-bearing hypno clips are NEVER observed here; their
    // pause/resume is owned end-to-end by the § 91 stop-surfaces + the GainNode
    // voice routing, and touching them re-opens the "voice loops forever" saga.
    // Resume runs through _tryPlay (which guards _beatLeft) gated on video.loop,
    // so a clip a stop-surface deliberately froze (loop=false / off-scene) is not
    // resurrected by scrolling back to it.
    _initVideoVisibilityObserver() {
        if (typeof IntersectionObserver === 'undefined') return;
        this._videoVisibilityObserver = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                // Isolated clips are observed via their host IFRAME (an observer
                // in this document cannot observe elements of the frame's
                // document) — map back to the inner <video>.
                const t = entry.target;
                const v = (t.tagName === 'IFRAME') ? t._pnoVideo : t;
                if (!v) continue;
                try {
                    if (entry.isIntersecting) {
                        if (typeof v._pnoResumeIfVisible === 'function') v._pnoResumeIfVisible();
                    } else if (!v.paused) {
                        v.pause();   // silent + off-screen, stop decoding
                    }
                } catch (e) { /* detached mid-flight */ }
            }
        }, {
            root: this._storyEl,
            rootMargin: '100px 0px'  // keep playing just past the fold to avoid flicker
        });
    }

    // ------------------------------------------------------------------
    // Event Bindings
    // ------------------------------------------------------------------

    _bindEvents() {
        // P0505: snapshot children present BEFORE this refresh so a deferred
        // family-clear can surgically remove only pre-refresh content and
        // preserve tag-added wrappers (e.g. #img: appended before the clear
        // fired). Reference: story.js _processStoryContent + STORY_REFRESH_START.
        this._scope.on(Events.STORY_REFRESH_START, () => {
            this._videoAddedThisRefresh = false;
            this._sceneChangedThisRefresh = false;
            if (!this._contentEl) return;
            this._preRefreshChildren = new Set(this._contentEl.children);
        });
        this._scope.on(Events.STORY_CLEAR, (opts) => this._onClear(opts));
        this._scope.on(Events.STORY_APPEND, () => this._onAppend());
        // Orphan-nuke: STORY_STOP_MEDIA fires at refreshView-top on every leave.
        // audioManager.detachHypnoVideo() handles the single active clip; this
        // also kills any DETACHED hypno clip still emitting native audio (the
        // "invisible, won't-stop" loop). Belt-and-suspenders, idempotent.
        this._scope.on(Events.STORY_STOP_MEDIA, () => { try { _nukeHypnoOrphans(); } catch (e) {} });
        // BUG_PATTERNS § 91 (eighth surface — the catch-all). A hypno session
        // always exits to a SAME-LOCATION knot (-> ApartmentComputer desktop or
        // -> Apartment power-off): no STORY_LOCATION_CHANGED, and the hub renders
        // zero narration so STORY_APPEND never fires either, and content is KEPT
        // so STORY_CLEAR doesn't fire. Result: an unmuted inline hypno clip keeps
        // looping its audio with no surface re-muting it (foundby:VPS 0.2.8.2,
        // Android-WebView "surrender audio doesn't stop" + Firefox "couldn't turn
        // it off after closing"). If a refresh presents choices but rendered NO
        // fresh #gif this cycle, the player is no longer watching a clip — mute
        // any still-audible inline video. An active hypno beat re-adds its #gif
        // every cycle (each knot has its own), so _videoAddedThisRefresh keeps it
        // audible. Mute-only (not pause) preserves the intended muted scrollback
        // visual loop.
        this._scope.on(Events.STORY_CHOICES_READY, () => {
            if (!this._contentEl) return;
            // DEFINITIVE SCENE-CHANGE SWEEP (owner-requested 2026-06-16: "on scene
            // change, end ALL running videos/sound so it doesn't come back in the
            // next report"). When the location/scene changed this refresh, stop
            // every story video — inline (#story-content) AND the expand modal
            // (#image-modal) — UNCONDITIONALLY, ignoring _videoAddedThisRefresh.
            // This is the only surface that runs at END-of-refresh, i.e. AFTER
            // EMPTY_PANEL_RECOVERY (story.js:1149) re-creates a scrollback hypno
            // clip — that recovery sets _videoAddedThisRefresh=true, so every other
            // stop-surface treats it as the "active beat" and skips it, leaving it
            // autoplay+loop (the recurring "hypno music loops endlessly" class:
            // 0.2.7.6.3 / 0.2.8.2 / 0.2.8.3; repro EXIT D). The character-panel
            // nude-view loop is outside both containers, so it survives.
            if (this._sceneChangedThisRefresh) {
                this._sceneChangedThisRefresh = false;
                this._stopAllMedia();
                // Same Android-robust src release as the player-left-beat path below,
                // but only when no fresh #gif rendered this refresh (else this is an
                // active beat on a location change and must keep its src to play).
                if (!this._videoAddedThisRefresh) this._releaseVideos(this._contentEl);
                return;
            }
            // LOCATION-FINAL OFF-SCENE PAUSE (BUG_PATTERNS § 91, ninth surface —
            // foundby:VPS 0.2.8.4 "Good girl, stay caged" on the Sissy Acceptance
            // SECOND video). The 2-video hypno chain's exit (HypnoSissy_Flash ->
            // "Desktop" -> ApartmentComputer, a zero-narration hub) fires NEITHER
            // STORY_CLEAR nor STORY_LOCATION_CHANGED (verified via event probe:
            // only STORY_APPEND + STORY_CHOICES_READY), so _stopAllMedia / _onClear
            // / _onLocationKept never run. The one surface that DOES run while the
            // clip is attached — _onAppend's clipLoc pause — fires mid-choice-pick
            // while storyManager.currentLocation is STILL the leaving scene, so the
            // clip's clipLoc === curLoc and the pause is skipped; the clip is left
            // paused=false/loop=true (silent on Chrome, but resumes via PiP / refocus
            // / Firefox / Android, and replay stacks a 2nd out-of-sync loop). Here at
            // STORY_CHOICES_READY currentLocation is SETTLED, so re-assert the "at
            // most one playing clip — the active beat's" invariant: pause+mute every
            // clip stamped with a DIFFERENT scene. The active beat (clipLoc===curLoc)
            // keeps its loop. Runs unconditionally (before the _videoAddedThisRefresh
            // early-return) so it also catches a stale clip when a fresh #gif rendered
            // this refresh. Pinned by tests/regression/hypno-sissy-2nd-video-audio.spec.js.
            const _curLoc = (storyManager && storyManager.currentLocation) || '';
            this._contentEl.querySelectorAll('video').forEach(v => {
                try {
                    // The active SILENT beat keeps its muted loop even if its
                    // clipLoc lags the settled location (streaming divert-through:
                    // #gif rendered in Stream_Action_X, flow settled at
                    // Stream_RoundEnd). Only audio-bearing clips get the hard
                    // off-scene pause here. foundby:Inciteful 0.2.9.
                    if (this._isActiveSilentClip(v)) return;
                    const clipLoc = v.dataset ? v.dataset.clipLoc : undefined;
                    if (clipLoc !== _curLoc) {
                        v.muted = true;
                        v.loop = false;            // a frozen off-scene clip must not re-loop
                        v.pause();                 // unconditional — a pending autoplay must not win
                    }
                } catch (e) { /* detached mid-flight */ }
            });
            // PLAYER LEFT THE BEAT (no fresh #gif rendered this refresh). This IS
            // the hub-exit path that fires NEITHER STORY_CLEAR NOR
            // STORY_LOCATION_CHANGED, re-proven 2026-06-20 via a real-click event
            // probe driving HypnoSissy_Flash --"Desktop"--> ApartmentComputer: the
            // ONLY events are append@<leaving-scene> + choicesReady@<hub>, and
            // _stopAllMedia was confirmed NOT called (stopAllCalled===0). So the
            // ONLY surface that ran on the leave was the inline off-scene pause
            // above (this._contentEl, INLINE ONLY), which left TWO holes the prior
            // five "fixes" never closed:
            //   1. The #image-modal expanded copy (a player who tapped the clip to
            //      fullscreen, then advanced via SMS/forced-event or Picture-in-
            //      Picture) is OUTSIDE this._contentEl, so it kept looping its voice
            //      at the desktop. Reproduced 100%: modal video paused=false /
            //      muted=false / loop=true at ApartmentComputer after the leave.
            //   2. The legacy PiP-exit here was gated on `anyUnmuted`, but the
            //      off-scene pause directly above ALREADY muted every inline clip,
            //      so anyUnmuted was always false and exitPictureInPicture() never
            //      fired, PiP audio (which survives a DOM mute) lived on.
            // Fix: run the SAME comprehensive chokepoint the scene-change branch
            // uses: _stopAllMedia sweeps inline (mute+pause+loop=false) AND the
            // #image-modal copy, exits Picture-in-Picture, and closes the modal.
            // Safe here because !_videoAddedThisRefresh reliably means "not watching
            // a clip" (an active hypno beat re-renders its own #gif every cycle, so
            // the flag is true during a session). The character-panel .nude-view loop
            // lives outside both roots and is untouched. Player report 0.2.8.4.2
            // ("turn on the sound in Hypno, click Continue, it keeps playing; play
            // again, the audio tracks stack on top of each other, even when turned off").
            if (this._videoAddedThisRefresh) return;
            this._stopAllMedia();
            // ANDROID-ROBUST RELEASE (player report 2026-06-20, Capacitor Android
            // WebView, screen-recording frame+audio analysis). On Chromium
            // _stopAllMedia's pause() stops the audio, but the inline clip KEEPS its
            // src (verified withSrc===1 at ApartmentComputer post-leave). On Android
            // WebView a paused-but-loop=true <video> that still holds its src keeps
            // the native MediaPlayer audio session alive, so the hypno voice plays on
            // at the desktop and resumes after the next clear (audio envelope: sound
            // 4.25-12.25s through the Desktop leave, brief gap at the Apartment clear,
            // then resumes 13s-end). Releasing the resource (removeAttribute('src') +
            // load(), the same teardown _onClear already uses via _releaseVideos) is
            // the only reliable cross-engine stop. Safe here: !_videoAddedThisRefresh
            // means the player left the beat, so no inline clip is the active session;
            // the same-location hub keeps the (now src-less) card behind its #ui:
            // overlay, so there is no visible regression. .nude-view is outside _contentEl.
            this._releaseVideos(this._contentEl);
        });
        // Explicit, immediate stop when the player powers off the computer
        // (the "Shut Down" X). _exitComputer auto-clicks an exit choice, but that
        // can land on the same-location zero-narration refresh above; silence now
        // so the audio dies the instant the desktop closes, not a beat later.
        this._scope.on('computer:shutdown', () => this._silenceInlineVideos());
        // BUG_PATTERNS § 91 (fifth surface). When the player leaves a hypno
        // scene back to a ZERO-narration hub (e.g. ApartmentComputer), the
        // deferred family-clear is cancelled and STORY_CLEAR never fires — so
        // _onClear's modal-video sweep is skipped and an expanded + unmuted
        // modal hypno video keeps looping its voice for the rest of the
        // session. STORY_LOCATION_CHANGED is the only signal on that path.
        // (Inline scrollback videos are already force-muted by _onAppend, which
        // runs earlier in the same refresh — so we only sweep the modal here.)
        // VPS reports 0.2.7.6.3: "Music looping after hypnosis" / "HYPNO SOUND
        // IN THE BACKGROUND ON REPEAT".
        this._scope.on(Events.STORY_LOCATION_CHANGED, () => {
            // Mark a scene change so the end-of-refresh STORY_CHOICES_READY sweep
            // runs AFTER any EMPTY_PANEL_RECOVERY re-creates a clip. _onLocationKept
            // still runs now for the common (no-recovery) case.
            this._sceneChangedThisRefresh = true;
            this._onLocationKept();
        });
        this._scope.on(Events.STORY_INFO_TEXT, (text, lineId) => {
            this._addInfoText(this._l10n(lineId, text), lineId, text);
        });
        this._scope.on(Events.STORY_DIALOGUE, (speaker, text, portrait, lineId) => {
            this._addDialogue(speaker, this._l10n(lineId, text), portrait, lineId, text);
        });
        // Re-translate visible story text when language changes
        this._scope.on(Events.LOCALE_CHANGED, () => this._retranslateContent());
        this._scope.on(Events.TAG_IMAGE, (name) => this._addImage(name));
        this._scope.on(Events.TAG_VIDEO, (name) => this._addVideo(name));
        // Live re-resolve displayed scene images when equipment changes an
        // appearance axis (wig on/off, masc<->fem outfit). The scene image
        // renders once on knot entry; without this a player changing outfits
        // in the wardrobe overlay kept seeing the stale variant until the
        // next knot re-entry (player report 0.2.7.6.1 "still not having long
        // hair in mirror during clothes changing"). Debounced — equip swaps
        // fire unequip+equip+buffs bursts.
        this._scope.on(Events.ITEM_EQUIPPED, () => this._refreshImageVariants());
        this._scope.on(Events.ITEM_UNEQUIPPED, () => this._refreshImageVariants());
        this._scope.on(Events.BUFFS_RECALCULATED, () => this._refreshImageVariants());
        this._scope.on(Events.IMAGE_PRELOAD, (names) => {
            for (const name of names) this.preloadImage(name);
        });
        this._scope.on(Events.TAG_CHAR, (portrait) => {
            this._currentSpeaker = portrait;
        });
        this._scope.on(Events.TAG_MONOLOGUE, (text) => this._addMonologue(text));
        this._scope.on(Events.TAG_SHOT, (shotType) => this._setShot(shotType));
        this._scope.on(Events.STORY_TYPING_START, () => this._showTyping());
        this._scope.on(Events.STORY_TYPING_STOP, () => { this.skipCurrentBeat(); this._hideTyping(); });
        this._scope.on(Events.STORY_END, () => this._showEnd());
    }

    // ------------------------------------------------------------------
    // Public API
    // ------------------------------------------------------------------

    /**
     * Add raw text to the story area. Called by engine for each line.
     * @param {string} text
     * @param {string|null} speaker - resolved speaker name
     * @param {string|null} portrait - raw portrait ID from #char: tag
     */
    addLine(text, speaker, portrait) {
        if (!text || !text.trim()) return;

        if (speaker && speaker !== 'info') {
            this._addDialogue(speaker, text, portrait);
        } else {
            this._addInfoText(text);
        }

        // Track in history
        this.history.push({ speaker, text, timestamp: Date.now() });
        // Cap history to prevent unbounded memory growth
        if (this.history.length > 500) this.history.splice(0, this.history.length - 500);
    }

    /**
     * Clear the story content area (location transition).
     */
    clear() {
        this._onClear();
    }

    /**
     * Preload an image by name (creates a hidden Image() to warm the cache).
     * @param {string} name - image name without extension
     */
    preloadImage(name) {
        if (imageMissCache.has(name)) return;
        const path = this._resolveImagePath(name);
        if (!path) return;
        const img = new Image();
        img.src = path;
    }

    // ------------------------------------------------------------------
    // Private: Content Rendering
    // ------------------------------------------------------------------

    /**
     * Scroll-append mode: keep existing content, add separator, dim old cards.
     * Called before new content is appended after a choice selection.
     */
    _onAppend() {
        if (!this._contentEl) return;
        this._cancelTyping();
        this._removeMonologues();
        this._scrollAnchor = null; // Will be set to separator element

        // Force-mute scrollback videos. The hypno clips are the only shipped
        // videos WITH an audio track, and Firefox exposes a native
        // context-menu "Unmute" (+ a PiP toggle, shown precisely because the
        // track exists) on inline videos the game itself keeps muted. Once
        // the story advances, an unmuted looping clip otherwise keeps its
        // voice playing from the dimmed scrollback — and later from the
        // display:none collapsed container — for the rest of the session,
        // immune to the volume sliders (third surface of BUG_PATTERNS § 91;
        // VPS report 2026-06-06, 0.2.7.6.3 Free, Firefox/Windows: "after
        // hypno session, sound just keeps spamming all game"). Only the
        // active beat may be audible.
        // Mute scrollback always; PAUSE any clip that belongs to a DIFFERENT
        // scene than the one now rendering. Each clip is stamped with the knot it
        // was created at (video.dataset.clipLoc); the active beat's clip shares the
        // current location and stays playing, everything else stops. This is
        // location-based on purpose: on the accumulated-navigation path where the
        // family-clear is deferred-and-cancelled, _onAppend is the ONLY surface
        // that fires (no STORY_CLEAR / STORY_LOCATION_CHANGED — verified via
        // trace) AND _videoAddedThisRefresh is stale-true (STORY_REFRESH_START
        // didn't reset it), so neither flag is reliable. The retained hypno clip
        // from the previous scene otherwise keeps looping muted and resurfaces as
        // "hypno music loops endlessly". Pinned by
        // tests/regression/hypno-audio-loop-repro.spec.js EXIT D; owner-requested
        // definitive stop 2026-06-16.
        const curLoc = (storyManager && storyManager.currentLocation) || '';
        this._contentEl.querySelectorAll('video').forEach(v => {
            try {
                // v0.2.9: NEVER mute the active gain-routed hypno video — Chrome
                // taps createMediaElementSource AFTER element.muted, so muting it
                // silences the gain audio. The active CURRENT beat stays exempt;
                // a LEAVING gain clip (its scene no longer current) gets detached
                // here as a backup — this is the surface that fires on the
                // deferred-cancelled family-clear path, so it's where an orphaned
                // clip whose STORY_STOP_MEDIA didn't fire gets stopped.
                if (this._hypnoVideoIsCurrentBeat(v, curLoc)) return;
                if (!v.muted) v.muted = true;
                const clipLoc = v.dataset ? v.dataset.clipLoc : undefined;
                // Pause only AUDIO-bearing clips from a different scene — that's
                // the leaking-voice case this surface guards. SILENT clips keep
                // their muted visual loop (the intended scrollback gif). _addVideo
                // already removed any prior .story-video, so the remaining silent
                // clip is the ACTIVE beat; pausing it here on a streaming
                // divert-through (clipLoc=Stream_Action_X, curLoc settled at
                // Stream_RoundEnd by the time STORY_APPEND fires) was what stopped
                // streaming gifs autoplaying. _videoAddedThisRefresh is documented
                // unreliable on this surface, so gate on the audio track, not it.
                // foundby:Inciteful 0.2.9.
                if (v.dataset && v.dataset.hasVoice && clipLoc !== curLoc && !v.paused) v.pause();
            } catch (e) { /* detached mid-flight */ }
        });

        // Remove previous inline choices (they stay as ghost showing what was picked)
        const oldChoices = this._contentEl.querySelector('#choices-inline');
        if (oldChoices) oldChoices.remove();
        // Also remove continue indicators
        const oldContinue = this._contentEl.querySelector('.continue-indicator');
        if (oldContinue) oldContinue.remove();

        // Dim all existing cards
        this._contentEl.querySelectorAll('.card-active').forEach(el => {
            el.classList.remove('card-active');
        });

        // Add separator between old and new content (only if there IS old content)
        const hasContent = this._contentEl.querySelector('.info-card, .dialogue-card, .story-image-wrapper');
        if (hasContent) {
            const sep = document.createElement('div');
            sep.className = 'story-section-separator';
            // Context label from current location
            const loc = storyManager?.currentLocation || '';
            const style = LOCATION_STYLES[loc];
            if (style) {
                const label = document.createElement('span');
                label.className = 'story-section-label';
                label.textContent = style.name;
                label.style.color = style.color;
                sep.appendChild(label);
            }
            this._contentEl.appendChild(sep);
            this._scrollAnchor = sep; // Scroll to here when new content starts
        }

        // DOM management: collapse old content if too many cards
        this._collapseOldContent();
    }

    /**
     * Collapse old cards when DOM gets too large.
     * Keeps the last 50 visible, hides the rest in a collapsed container.
     */
    _collapseOldContent() {
        if (!this._contentEl) return;
        const cards = this._contentEl.querySelectorAll('.info-card, .dialogue-card, .story-image-wrapper, .story-section-separator');
        const MAX_VISIBLE = 60;
        if (cards.length <= MAX_VISIBLE) return;

        // Find or create collapsed container at the top
        let collapsed = this._contentEl.querySelector('.story-collapsed');
        if (!collapsed) {
            collapsed = document.createElement('div');
            collapsed.className = 'story-collapsed';
            this._contentEl.insertBefore(collapsed, this._contentEl.firstChild);

            // Add expand toggle button
            const _t = (k, fb) => { const v = L.get(k); return v !== k ? v : fb; };
            const showLabel = _t('ui.show_earlier', 'Show earlier...');
            const hideLabel = _t('ui.hide_earlier', 'Hide earlier');
            const toggle = document.createElement('button');
            toggle.className = 'story-collapsed-toggle';
            toggle.textContent = showLabel;
            toggle.addEventListener('click', () => {
                collapsed.classList.toggle('expanded');
                toggle.textContent = collapsed.classList.contains('expanded') ? hideLabel : showLabel;
            });
            this._contentEl.insertBefore(toggle, collapsed.nextSibling);
        }

        // Move excess cards into collapsed container
        // Spread to static array to avoid live NodeList shifting during iteration
        const cardArr = [...cards].filter(c => c !== collapsed && !c.closest('.story-collapsed'));
        const excess = cardArr.length - MAX_VISIBLE;
        for (let i = 0; i < excess; i++) {
            // Re-parenting an <iframe> reloads it as a fresh blank document —
            // the isolated clip would silently become an empty box AND its
            // media teardown would race the reload. Release it cleanly first
            // (frame removed, blur still resurfaces as the collapsed visual).
            try { this._releaseVideos(cardArr[i]); } catch (e) { /* defensive */ }
            collapsed.appendChild(cardArr[i]);
        }

        // appendChild re-parents without pausing, and display:none does NOT
        // stop a <video> — a clip moved into the hidden container keeps
        // looping (worst case: audibly, if the player unmuted it via the
        // browser's native controls). Muted visual loops in the VISIBLE
        // scrollback are fine; invisible ones get silenced + stopped.
        collapsed.querySelectorAll('video').forEach(v => {
            if (this._hypnoVideoIsCurrentBeat(v)) return; // gain audio; leaving clip detached as backup
            try { v.muted = true; v.pause(); } catch (e) { /* detached mid-flight */ }
        });
    }

    _onLocationKept() {
        // BUG_PATTERNS § 91 (fifth surface). Mirrors the modal-video sweep in
        // _onClear, but for the cancelled-deferred-clear path (story.js): the
        // location changed and content was KEPT, so no STORY_CLEAR fires.
        // Without this, an expanded + unmuted hypno modal video loops its voice
        // forever after the player returns to a zero-narration hub. Idempotent:
        // returns 0 when the modal is already empty.
        if (this._teardownModalVideos() > 0) {
            const modal = document.getElementById('image-modal');
            if (modal) modal.classList.add('hidden');
            document.body.style.overflow = '';
        }
        // § 91 (sixth surface). INLINE story videos are NOT silenced on this
        // path: STORY_APPEND never fires for a zero-narration hub re-entry, so
        // _onAppend's scrollback-mute is skipped. A Firefox player who did the
        // highest computer-hypno session, unmuted the inline clip via the
        // native context menu, then walked to a path-driven hub (ClinicHub,
        // CHOICES_WITHOUT_NARRATION) kept hearing the loop with no surface
        // re-muting it. foundby:VPS player 0.2.8 Free, Firefox/Mac.
        this._silenceInlineVideos();
    }

    /**
     * Backup hypno-audio stop for the visual sweeps. The sweeps must NEVER mute
     * the active gain-routed beat (Chrome taps createMediaElementSource AFTER
     * element.muted, so muting silences the gain). Normally STORY_STOP_MEDIA at
     * refreshView-top detaches a LEAVING clip before any sweep runs — but a
     * leave path that doesn't run refreshView (deferred-cancelled family-clear,
     * map/quicktravel, screen change) can leave the orphaned clip still routed,
     * so its audio outlives its (now-removed) card with no 🔊 button to stop it
     * (the recurring "hypno audio won't stop" report). So: if the active gain
     * video belongs to a scene that is no longer current, detach it here as a
     * backup and let the caller mute/pause the element. The true current beat
     * (clipLoc === current) stays exempt.
     * @returns {boolean} true = this IS the current beat, caller must leave it alone.
     */
    _hypnoVideoIsCurrentBeat(v, curLoc) {
        if (v !== audioManager._hypnoVideoEl) return false;
        try {
            const clipLoc = v.dataset ? v.dataset.clipLoc : undefined;
            const loc = (curLoc !== undefined)
                ? curLoc
                : ((storyManager && storyManager.currentLocation) || '');
            if (clipLoc !== undefined && clipLoc !== loc) {
                audioManager.detachHypnoVideo();   // orphaned/leaving clip — backup stop
                return false;                       // now safe to mute/pause the element
            }
        } catch (e) { /* defensive */ }
        return true;   // active current beat — exempt from element-muting
    }

    /**
     * Definitively stop EVERY story/scene video on a scene change: pause + mute
     * every clip in #story-content (inline) AND #image-modal (the expanded
     * fullscreen copy), exit Picture-in-Picture, and close the expand modal.
     * Pause (not just mute) is the key — the hypno clips are `loop`, so a
     * muted-but-still-playing clip keeps running and can resume audio later
     * (focus/gesture/PiP, or Android-WebView/Firefox where DOM mute doesn't stick
     * to a looping element). The character-panel nude-view loop lives in
     * `.nude-view`, outside both containers, so it is deliberately untouched.
     * This is the single chokepoint that ends the recurring "hypno music loops
     * endlessly" class once and for all (owner-requested 2026-06-16).
     */
    // A SILENT story clip that was just rendered THIS refresh is the active
    // visual beat and must keep its muted autoplay loop. The §91 stop-surfaces
    // (_stopAllMedia + the off-scene clipLoc pause) force pause + loop=false to
    // kill a LEAKING HYPNO VOICE — a silent clip has no audio to leak. Because
    // _addVideo removes every prior .story-video before attaching the new one,
    // "a clip was added this refresh" ⟺ "the single inline clip is the active
    // beat". Streaming broke without this: each #gif action knot diverts on to
    // Stream_RoundEnd before choices render, so the clip's clipLoc never equals
    // the settled location → it was paused + un-looped the instant it appeared
    // (foundby:Inciteful 0.2.9 "only one autoplays, the rest play once and don't
    // loop"). hasVoice clips (laptop hypno + HypnoMind flashes) are NEVER
    // exempted here, so every audio-leak fix is preserved. Mirrors the
    // _hypnoVideoIsCurrentBeat exemption, for the silent case.
    _isActiveSilentClip(v) {
        try {
            return this._videoAddedThisRefresh === true
                && v && v.dataset && !v.dataset.hasVoice
                && v.classList && v.classList.contains('story-video')
                && document.contains(v);
        } catch (e) { return false; }
    }

    _stopAllMedia() {
        // NOTE (v0.2.9): the hypno audio detach is NOT done here. It is owned by
        // the deterministic STORY_STOP_MEDIA at refreshView-top (race-free) + the
        // background suspend + the Skip button. Detaching here would kill the
        // ACTIVE beat's audio, because the deferred family-clear runs _onClear ->
        // _silenceInlineVideos -> _stopAllMedia AFTER _addVideo attaches the new
        // beat. This method only pauses/mutes the <video> visuals below.
        const roots = [this._contentEl, document.getElementById('image-modal')];
        for (const root of roots) {
            if (!root) continue;
            root.querySelectorAll('video').forEach(v => {
                try {
                    // v0.2.9: exempt the active CURRENT gain-routed hypno beat
                    // (muting/pausing the element silences its gain audio). A
                    // leaving gain clip (scene no longer current) is detached as
                    // a backup, then muted/paused below.
                    if (this._hypnoVideoIsCurrentBeat(v)) return;
                    if (this._isActiveSilentClip(v)) return;   // active streaming/silent beat keeps its muted loop
                    v.muted = true;
                    v.loop = false;          // even if a deferred play() fires, it can't loop endlessly
                    if (!v.paused) v.pause();
                } catch (e) { /* detached mid-flight */ }
            });
        }
        try {
            if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});
        } catch (e) { /* PiP unsupported (Safari uses webkit presentation mode) */ }
        try {
            // § 106 frame-hosted modal clip: the modal is closed below, so a
            // full frame teardown (not just pause) is always correct here.
            releaseClipsIn(document.getElementById('image-modal'), 'iframe.image-modal-img');
        } catch (e) { /* defensive */ }
        try {
            const modal = document.getElementById('image-modal');
            if (modal && !modal.classList.contains('hidden')) {
                modal.classList.add('hidden');
                document.body.style.overflow = '';
            }
        } catch (e) { /* defensive */ }
    }

    /**
     * Mute + pause every inline story <video> and pop any clip out of
     * Picture-in-Picture. Firefox/Chrome PiP detaches the audio track into an
     * OS-level window that survives a DOM `muted = true`, so exiting PiP is the
     * only reliable stop for a player who popped the hypno clip out. Guarded —
     * Safari exposes a webkit presentation mode instead of the standard API.
     */
    _silenceInlineVideos() {
        // v0.2.9: no hypno-audio detach here (see _stopAllMedia note) — it would
        // kill the active beat via the deferred family-clear. Visual pause only.
        if (!this._contentEl) return;
        this._contentEl.querySelectorAll('video').forEach(v => {
            // v0.2.9: exempt the active CURRENT gain beat; detach a leaving one (see _stopAllMedia).
            if (this._hypnoVideoIsCurrentBeat(v)) return;
            try { v.muted = true; v.pause(); } catch (e) { /* detached mid-flight */ }
        });
        try {
            if (document.pictureInPictureElement) {
                document.exitPictureInPicture().catch(() => {});
            }
        } catch (e) { /* PiP API unsupported */ }
    }

    /**
     * Fully release every <video> inside `root` BEFORE its card is detached.
     * Pause + mute stops the audio, but the element keeps its decoded media
     * buffer + src resource in memory until GC — and a long session
     * (computer/hypno binge) detaches dozens of looping clips, so on a phone
     * the orphaned buffers pile up until the tab is killed for memory. Clearing
     * the src and calling load() aborts the resource selection and frees the
     * buffer immediately, the canonical "stop + release a media element"
     * teardown. Only call on cards that are ACTUALLY being removed — never on a
     * survivor (the current beat's freshly-rendered clip).
     */
    _releaseVideos(root) {
        if (!root) return;
        root.querySelectorAll('video').forEach(v => this._hardReleaseVideo(v));
        // Isolated clips (leak workaround, see _addVideo): their <video> lives
        // inside a same-origin playback iframe — invisible to the querySelector
        // above. Removing the FRAME destroys its document and with it the
        // WebMediaPlayer + decoded-frame pool (the part the classic
        // removeAttribute('src')+load() teardown provably does NOT free —
        // BUG_PATTERNS § 106).
        root.querySelectorAll('iframe.story-video-frame').forEach(fr => {
            const v = this._frameInnerVideo(fr);
            if (v) this._hardReleaseVideo(v);
            else {
                try { this._videoVisibilityObserver?.unobserve(fr); fr.remove(); } catch (e) { /* detached */ }
            }
        });
    }

    /** Inner <video> of an isolated playback frame (same-origin). */
    _frameInnerVideo(fr) {
        if (fr._pnoVideo) return fr._pnoVideo;
        try { return fr.contentDocument?.querySelector('video') || null; } catch (e) { return null; }
    }

    /**
     * Canonical single-clip teardown. Inline clip: unobserve + pause + strip
     * src + load() (frees the demuxer; the decoded-frame pool is freed by GC
     * for main-document clips ONLY when Chromium feels like it — see § 106).
     * Isolated clip: additionally REMOVE its host iframe — document teardown
     * is the one mechanism that deterministically frees the media pools. The
     * wrapper's `video-loaded` class is dropped so the blur still resurfaces
     * as the scrollback visual where the clip used to loop.
     */
    _hardReleaseVideo(v) {
        try {
            this._videoVisibilityObserver?.unobserve(v);
            let frameEl = null;
            try {
                if (v.ownerDocument !== document) frameEl = v.ownerDocument.defaultView?.frameElement || null;
            } catch (e) { /* dead frame — nothing left to release */ }
            v.pause();
            v.muted = true;
            v.removeAttribute('src');
            v.load();              // aborts the current resource, frees the buffer
            if (frameEl) {
                this._videoVisibilityObserver?.unobserve(frameEl);
                const w = frameEl.closest('.story-image-wrapper');
                frameEl.remove();  // destroys the playback document → frees player + frame pool
                if (w) w.classList.remove('video-loaded');
            }
        } catch (e) { /* detached mid-flight */ }
    }

    _onClear(opts) {
        if (!this._contentEl) return;
        // BUG_PATTERNS § 91 (eighth surface). Silence every inline clip BEFORE
        // the surgical removal below detaches its card. A location-change clear
        // (player closes the computer overlay / leaves the hypno portal) fires
        // STORY_CLEAR but never STORY_APPEND, so _onAppend's force-mute never
        // runs — and removing a still-playing <video> from the DOM neither
        // pauses nor mutes it: a detached node keeps looping its audio track
        // until GC, invisible to every querySelectorAll sweep. A Beginner Pack
        // session stacks several #gif beats, so this must mute ALL of them
        // (querySelectorAll over _contentEl, run while still attached), not
        // just the active one. foundby:VPS player 0.2.8.1.2 Free, Windows
        // ("sound's looping despite me being out of the vid ... going back in
        // doubled the sound").
        this._silenceInlineVideos();
        this._cancelTyping();
        this._removeMonologues();
        this._scrollAnchor = null; // Clear mode: scroll to top
        // Panel clear removes scrolled-out cards (and their blob: srcs) from
        // the DOM — the safe moment to reclaim LRU-evicted blob URLs that
        // were deferred because a card still referenced them. Run after the
        // current call stack so the card removal below has happened.
        setTimeout(() => { try { sweepEvictedBlobs(); } catch (e) { /* non-fatal */ } }, 0);
        // Defense-in-depth: a location change / save load must never leave a
        // stray expanded video playing audio behind a hidden modal. If the
        // sweep removed one while the modal was open, hide the modal too —
        // the scene it belonged to is gone.
        if (this._teardownModalVideos() > 0) {
            const modal = document.getElementById('image-modal');
            if (modal) modal.classList.add('hidden');
            document.body.style.overflow = '';
        }
        // P0505: surgical clear — remove only children that existed BEFORE this
        // refresh (the previous knot's content). Preserves tag-added wrappers
        // (e.g. #img:master_morning_inspection appended in the same refresh
        // before this clear fired). Without this the clear wipes the new
        // knot's image along with the previous knot's text.
        if (opts?.preserveTagAddedContent && this._preRefreshChildren) {
            const baseline = this._preRefreshChildren;
            // Detach baseline children; survivors stay in DOM order
            for (const child of Array.from(this._contentEl.children)) {
                if (baseline.has(child)) {
                    this._releaseVideos(child);  // free media buffers before detaching (mobile memory)
                    child.remove();
                }
            }
            // _storyImages: drop refs to wiped images, keep refs to survivors
            if (Array.isArray(this._storyImages)) {
                this._storyImages = this._storyImages.filter(img => img.isConnected);
            }
            // New baseline = whatever survived (so a second deferred clear in
            // the same refresh — rare but possible — also surgically clears).
            this._preRefreshChildren = new Set(this._contentEl.children);
        } else {
            this._releaseVideos(this._contentEl);  // free all media buffers before wiping (mobile memory)
            this._contentEl.innerHTML = '';
            this._storyImages = [];
            this._preRefreshChildren = null;
        }
        this._currentSpeaker = null;
        this._lastPortrait = null;
        this._pendingShot = null;
        // Exit cinema mode on any scene/location change — hero shots are one-beat
        document.body.classList.remove('cinema-hero');

        // Scroll to top after clearing (new location = fresh start)
        if (this._storyEl) {
            this._storyEl.scrollTop = 0;
        }

        // Location header
        const loc = storyManager?.currentLocation || '';

        // Apply per-location accent class to scroll container
        if (this._storyEl) {
            this._storyEl.className = this._storyEl.className.replace(/\bstory-area-\w+/g, '').trim();
            const area = LOCATION_AREA_MAP[loc];
            if (area) this._storyEl.classList.add(`story-area-${area}`);
        }

        const style = LOCATION_STYLES[loc];
        if (style) {
            const header = document.createElement('div');
            header.className = 'story-location-header';
            header.style.borderLeftColor = style.color;
            header.innerHTML = `<span class="story-location-name" style="color:${style.color}">${style.name}</span>`;
            // P0505: in surgical-clear mode (preserveTagAddedContent), the
            // tag-added wrappers are already in the panel — the new header
            // belongs ABOVE them. In normal clear mode the panel is empty
            // and append/prepend behave identically.
            if (this._contentEl.firstChild) {
                this._contentEl.insertBefore(header, this._contentEl.firstChild);
            } else {
                this._contentEl.appendChild(header);
            }
        }
    }

    /** Try to translate text via lineId key. Falls back to original text. */
    _l10n(lineId, text) {
        return L.translateLine(lineId, text);
    }

    /** Re-translate all visible story cards when language changes. */
    _retranslateContent() {
        if (!this._contentEl) return;
        this._contentEl.querySelectorAll('[data-line-id]').forEach(card => {
            const lineId = card.dataset.lineId;
            const original = card.dataset.originalText || '';
            const translated = this._l10n(lineId, original);
            const textEl = card.querySelector('.info-text, .dialogue-text');
            if (textEl) textEl.innerHTML = this._processText(translated);
        });
    }

    _addInfoText(text, lineId, originalText) {
        if (!this._contentEl) return;

        // Long-text-trim (Pablito 2026-05-05): wall-of-text narration cards
        // ≥ 800 chars get auto-split at the nearest sentence boundary, so the
        // player sees two readable cards instead of one. Author intent is
        // preserved when there is no natural split in the middle window —
        // the text is left as a single card with the long-card hint class.
        const parts = this._maybeSplitLongText(text);
        if (parts.length > 1) {
            // First half: emit synchronously WITHOUT lineId/originalText so
            // the locale re-translate pass doesn't double-replace both halves
            // with the full translated string. The L10n key for the whole
            // line is kept on the second card (the one that ends the beat).
            this._renderInfoCard(parts[0], null, null, false);
            this._renderInfoCard(parts[1], lineId, originalText, false);
        } else {
            this._renderInfoCard(text, lineId, originalText, true);
        }

        // ENG-09: prune oldest cards past MAX_CARDS to keep DOM bounded.
        // Long Day-30+ runs accumulated 500+ cards → GC-pauses + scroll-lag.
        // 250 keeps ~3 screens of scrollback (plenty for "wait, what just
        // happened?" backtracking) while bounding memory growth.
        this._pruneOldCards(250);
    }

    /**
     * Render a single info-card. Extracted from _addInfoText so the long-text
     * split path can emit two cards back-to-back.
     * @param {string} text - already-l10n-resolved text
     * @param {string|null} lineId
     * @param {string|null} originalText
     * @param {boolean} allowLongHint - add .long-card hint class when text >1500 chars
     */
    _renderInfoCard(text, lineId, originalText, allowLongHint) {
        // Dim previous active cards
        this._dimPreviousCards();

        const card = document.createElement('div');
        card.className = 'info-card card-active';
        if (lineId) card.dataset.lineId = lineId;
        if (originalText) card.dataset.originalText = originalText;

        // Visual hint for very long, unsplittable cards (Pablito feedback).
        if (allowLongHint && text && text.length > 1500) {
            card.classList.add('long-card');
        }

        // Stagger animation — count only active (new) cards for delay
        const activeCards = this._contentEl.querySelectorAll('.card-active').length;
        card.style.animationDelay = `${Math.min(activeCards * 0.05, 0.15)}s`;

        const textEl = document.createElement('div');
        textEl.className = 'info-text';

        card.appendChild(textEl);
        this._contentEl.appendChild(card);
        this._typeText(textEl, this._processText(text));
    }

    /**
     * Try to split a long narration string at the natural sentence boundary
     * closest to the midpoint. Returns [whole] when no clean split exists in
     * the middle window. Single ornate sentences stay intact (respect author
     * intent). Returns [first, second] when a split is applied.
     *
     * Triggers only at 800+ chars. Both halves must be ≥ MIN_HALF (200) chars,
     * so we never split a slightly-over-threshold line near its edges.
     *
     * @param {string} text
     * @returns {string[]} 1- or 2-element array
     */
    _maybeSplitLongText(text) {
        if (!text || text.length < 800) return [text];
        const MIN_HALF = 200;
        const len = text.length;
        const mid = Math.floor(len / 2);
        // Sentence boundary = closing punctuation followed by space/quote/newline.
        // Also accept '\n' alone (markdown-style paragraph break).
        const boundary = /([.!?])(["')\]]?)(\s|\n)|\n/g;
        let best = -1;
        let bestDist = Infinity;
        let m;
        while ((m = boundary.exec(text)) !== null) {
            // Position right after the boundary (start of next sentence).
            const splitAt = m.index + m[0].length;
            if (splitAt < MIN_HALF) continue;
            if (len - splitAt < MIN_HALF) break;
            const d = Math.abs(splitAt - mid);
            if (d < bestDist) {
                bestDist = d;
                best = splitAt;
            }
        }
        if (best < 0) return [text];
        return [text.slice(0, best).trimEnd(), text.slice(best).trimStart()];
    }

    /** Remove oldest scroll-append cards beyond `max` to keep the DOM bounded. */
    _pruneOldCards(max) {
        if (!this._contentEl) return;
        const cards = this._contentEl.querySelectorAll('.info-card, .dialogue-card, .story-image-wrapper, .story-section-separator');
        const excess = cards.length - max;
        if (excess <= 0) return;
        const prunedImgs = new Set();
        for (let i = 0; i < excess; i++) {
            const el = cards[i];
            // Full media teardown, mirroring _releaseVideos/_onClear semantics.
            // Pre-fix this path did `m.src = ''` only: pruned media stayed
            // registered with the IntersectionObservers (observers hold strong
            // refs, so every pruned <video>/<img> was retained detached —
            // video decoder + frame buffers included) and _storyImages kept
            // phantom entries the modal gallery kept scanning. Leak-class
            // player report 2026-07-02 ("performance gets worse over time,
            // reload is only a temp fix").
            const media = el.querySelectorAll ? el.querySelectorAll('img, video, iframe.story-video-frame') : [];
            media.forEach(m => {
                try {
                    if (m.tagName === 'IFRAME') {
                        const v = this._frameInnerVideo(m);
                        if (v) this._hardReleaseVideo(m._pnoVideo || v);
                        else { this._videoVisibilityObserver?.unobserve(m); m.remove(); }
                    } else if (m.tagName === 'VIDEO') {
                        this._videoVisibilityObserver?.unobserve(m);
                        m.pause();
                        m.muted = true;
                        m.removeAttribute('src');
                        m.load();              // aborts the resource, frees the buffer
                    } else {
                        this._lazyObserver?.unobserve(m);
                        m.src = '';
                        prunedImgs.add(m);
                    }
                } catch (e) { /* detached mid-flight */ }
            });
            el.remove();
        }
        // Drop pruned imgs from _storyImages so the modal gallery's linear
        // scans don't walk (and pin) detached elements for the whole session.
        if (prunedImgs.size && Array.isArray(this._storyImages)) {
            this._storyImages = this._storyImages.filter(img => !prunedImgs.has(img));
        }
        // Pruned cards may have held the last DOM refs to LRU-evicted blob
        // URLs — same deferred reclaim moment as _onClear (§36 update).
        setTimeout(() => { try { sweepEvictedBlobs(); } catch (e) { /* non-fatal */ } }, 0);
    }

    /** Dim all previous cards so only the newest one is fully opaque. */
    _dimPreviousCards() {
        if (!this._contentEl) return;
        this._contentEl.querySelectorAll('.card-active').forEach(el => {
            el.classList.remove('card-active');
        });
    }

    _addDialogue(speaker, text, portrait, lineId, originalText) {
        if (!this._contentEl) return;

        // Dim previous active cards
        this._dimPreviousCards();

        const card = document.createElement('div');
        card.className = 'dialogue-card card-active';

        // Determine speaker key for styling (strip emotion suffix)
        const speakerKey = portrait ? portrait.split('_')[0] : (speaker || '').toLowerCase();
        card.dataset.speaker = speakerKey;

        // Consecutive same-speaker detection: compact threading (hide tail + portrait)
        // Look at the last dialogue card in the content area, ignoring separators/images.
        const allCards = this._contentEl.querySelectorAll('.dialogue-card');
        const prevCard = allCards[allCards.length - 1];
        if (prevCard && prevCard.dataset.speaker === speakerKey) {
            // Only count as consecutive if no image/separator came between
            let next = prevCard.nextElementSibling;
            let interrupted = false;
            while (next) {
                if (next.classList.contains('story-image-wrapper') ||
                    next.classList.contains('story-section-separator')) {
                    interrupted = true;
                    break;
                }
                next = next.nextElementSibling;
            }
            if (!interrupted) card.classList.add('same-speaker');
        }
        if (lineId) card.dataset.lineId = lineId;
        if (originalText) card.dataset.originalText = originalText;

        // Stagger animation — count only active (new) cards for delay
        const activeCards = this._contentEl.querySelectorAll('.card-active').length;
        card.style.animationDelay = `${Math.min(activeCards * 0.05, 0.15)}s`;

        // Portrait
        const portraitEl = document.createElement('div');
        portraitEl.className = 'dialogue-portrait';

        // Try to load portrait image, fallback to letter
        const letter = document.createElement('span');
        letter.className = 'dialogue-portrait-letter';
        letter.textContent = (speaker || '?')[0].toUpperCase();
        portraitEl.appendChild(letter);

        // Track portrait changes for crossfade effect
        const isSameSpeaker = speakerKey === (this._lastPortrait?.split('_')[0] || '');
        const isNewEmotion = portrait && portrait !== this._lastPortrait;

        if (portrait) {
            const img = document.createElement('img');
            const blobUrl = (typeof isTarMode === 'function' && isTarMode() && typeof getImageUrl === 'function') ? getImageUrl(portrait) : null;
            img.src = blobUrl || `assets/images/Portraits/${portrait}.png`;
            img.alt = speaker || '';
            img.onerror = () => {
                // Same broken-blob-store class as scene images: one RAM-only
                // data: URI retry before dropping the portrait (2026-07-11).
                if (isTarMode() && !img.dataset.pnoDataRetry) {
                    img.dataset.pnoDataRetry = '1';
                    getImageDataUri(portrait).then((uri) => {
                        if (uri) { img.src = uri; return; }
                        _warnRenderFail('portrait', portrait, img.src);
                        img.remove();
                    }).catch(() => { img.remove(); });
                    return;
                }
                _warnRenderFail('portrait', portrait, img.src);
                img.remove();
            };
            img.onload = () => {
                letter.style.display = 'none';
                // Crossfade: same NPC, different emotion = subtle swap animation
                if (isSameSpeaker && isNewEmotion) {
                    img.classList.add('portrait-swap');
                    img.addEventListener('animationend', () => img.classList.remove('portrait-swap'), { once: true });
                }
            };
            portraitEl.insertBefore(img, letter);
            this._lastPortrait = portrait;
        }

        card.appendChild(portraitEl);

        // Body
        const body = document.createElement('div');
        body.className = 'dialogue-body';

        const nameEl = document.createElement('div');
        nameEl.className = 'dialogue-name';
        nameEl.textContent = speaker || 'Unknown';
        body.appendChild(nameEl);

        const textEl = document.createElement('div');
        textEl.className = 'dialogue-text';
        body.appendChild(textEl);

        card.appendChild(body);
        this._contentEl.appendChild(card);
        this._typeText(textEl, this._processText(text));

        // ENG-09 cap (same as _addInfoText): dialogue-heavy stretches
        // (streaming scenes, dates) previously only pruned when the next
        // info card happened to land — cards could drift past the bound.
        this._pruneOldCards(250);
    }

    /**
     * Set the shot type for the next image. Tag #shot:wide|close|hero|detail
     * fires before #img: in the Ink stream, we cache it here and apply on
     * the next _addImage / _addVideo call.
     */
    _setShot(shotType) {
        const valid = ['wide', 'close', 'hero', 'detail'];
        if (valid.includes(shotType)) {
            this._pendingShot = shotType;
        }
    }

    _consumePendingShot() {
        const shot = this._pendingShot || 'wide';
        this._pendingShot = null;
        // Cinema mode: hero shots + scene-mode = near-fullscreen dramatic presentation.
        // Body class drives CSS in _04-story-choices.css. Respects scene-mode-disabled.
        const sceneModeOn = !document.body.classList.contains('scene-mode-disabled');
        if (shot === 'hero' && sceneModeOn) {
            document.body.classList.add('cinema-hero');
        } else {
            document.body.classList.remove('cinema-hero');
        }
        return shot;
    }

    /**
     * Re-resolve appearance variants of all displayed scene images and swap
     * src where the variant changed (e.g. wig equipped while the wardrobe
     * overlay is open over the apartment_mirror scene). Debounced: equip
     * actions emit ITEM_UNEQUIPPED + ITEM_EQUIPPED + BUFFS_RECALCULATED in
     * one burst, and the Ink flag pushes land within the same tick.
     */
    _refreshImageVariants() {
        if (!this._contentEl) return;
        clearTimeout(this._variantRefreshTimer);
        this._variantRefreshTimer = setTimeout(() => {
            const imgs = this._contentEl.querySelectorAll('img.story-image[data-image-name]');
            for (const img of imgs) {
                const base = img.dataset.imageName;
                let newVariant;
                try { newVariant = appearanceState.resolveVariantName(base); } catch (e) { continue; }
                if (!newVariant || newVariant === img.dataset.resolvedVariant) continue;
                const path = this._resolveImagePath(base);
                if (!path) continue;
                img.dataset.resolvedVariant = newVariant;
                img.src = path;
            }
        }, 120);
    }

    _addImage(name) {
        if (!this._contentEl) return;

        const resolvedPath = this._resolveImagePath(name);
        if (!resolvedPath) {
            // While the tar is incomplete/healing, park a placeholder card
            // that the pno-tar-healed listener fills in once the background
            // re-download lands — pre-fix the scene just silently had no
            // image and stayed that way even after the heal. Outside that
            // state (name genuinely unknown) keep the silent skip.
            let tarIncomplete = false;
            try {
                tarIncomplete = isTarMode() && window.__tarStatus && !window.__tarStatus().complete;
            } catch (e) { /* treat as complete */ }
            if (!tarIncomplete) return;

            const shotP = this._consumePendingShot();
            const wrapper = document.createElement('div');
            wrapper.className = `story-image-wrapper loading shot-${shotP}`;
            wrapper.dataset.pendingImageName = name;
            const ph = document.createElement('div');
            ph.className = 'story-image-placeholder';
            ph.innerHTML = `
                <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg">
                    <rect width="400" height="250" fill="#1a1428"/>
                    <text x="200" y="115" text-anchor="middle" font-family="sans-serif" font-size="13" fill="#887ca0">Loading image...</text>
                    <text x="200" y="140" text-anchor="middle" font-family="monospace" font-size="10" fill="#5a4e6e">${name}</text>
                    <circle cx="200" cy="175" r="10" fill="none" stroke="#6e3c7a" stroke-width="2" stroke-dasharray="25 12">
                        <animateTransform attributeName="transform" type="rotate" from="0 200 175" to="360 200 175" dur="1s" repeatCount="indefinite"/>
                    </circle>
                </svg>`;
            wrapper.appendChild(ph);
            this._contentEl.appendChild(wrapper);
            this._pendingHealImages.push({ name, wrapper });
            if (this._pendingHealImages.length > 30) this._pendingHealImages.shift();
            return;
        }

        const shot = this._consumePendingShot();

        // Divider before
        const divBefore = document.createElement('div');
        divBefore.className = 'story-image-divider';
        this._contentEl.appendChild(divBefore);

        // Image wrapper — show placeholder immediately, swap when loaded
        const wrapper = document.createElement('div');
        wrapper.className = `story-image-wrapper loading shot-${shot}`;

        // Placeholder SVG (shown until real image loads)
        const placeholder = document.createElement('div');
        placeholder.className = 'story-image-placeholder';
        placeholder.innerHTML = `
            <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg">
                <rect width="400" height="250" fill="#1a1428"/>
                <text x="200" y="115" text-anchor="middle" font-family="sans-serif" font-size="13" fill="#887ca0">Loading image...</text>
                <text x="200" y="140" text-anchor="middle" font-family="monospace" font-size="10" fill="#5a4e6e">${name}</text>
                <circle cx="200" cy="175" r="10" fill="none" stroke="#6e3c7a" stroke-width="2" stroke-dasharray="25 12">
                    <animateTransform attributeName="transform" type="rotate" from="0 200 175" to="360 200 175" dur="1s" repeatCount="indefinite"/>
                </circle>
            </svg>`;
        wrapper.appendChild(placeholder);

        // Real image (loads in background)
        const img = document.createElement('img');
        img.className = 'story-image';
        img.alt = name.replace(/_/g, ' ');
        img.dataset.imageName = name;
        // Remember which appearance variant this render resolved to, so the
        // equip-driven live refresh can swap src only when the variant
        // actually changed (cheap string compare, no URL-form issues).
        img.dataset.resolvedVariant = appearanceState.resolveVariantName(name);
        img.decoding = 'async';
        img.fetchPriority = 'high';
        img.style.display = 'none';

        // Reveal image after decode completes (prevents main-thread jank)
        const revealImage = () => {
            placeholder.remove();
            img.style.display = '';
            wrapper.classList.remove('loading');
            if (typeof gsap !== 'undefined') {
                gsap.fromTo(img, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power3.out' });
            }
            this._scrollToBottom();
        };

        img.addEventListener('load', () => {
            // Use decode() to avoid blocking the main thread during image decode
            if (typeof img.decode === 'function') {
                img.decode().then(revealImage).catch(revealImage);
            } else {
                revealImage();
            }
        }, { once: true });

        // Track for modal navigation
        if (!this._storyImages) this._storyImages = [];
        this._storyImages.push(img);

        // Click to expand
        img.addEventListener('click', () => {
            this._expandImage(img.src);
        });

        // Fallback chain
        this._setupImageFallback(img, name);

        // Load image immediately (tar mode = blob URLs already in memory,
        // non-tar mode = browser HTTP cache handles dedup). Compat mode
        // (broken blob store, player-enabled via the render-fail banner):
        // go straight to the RAM-only data: URI, blob URL only as fallback.
        if (isTarMode() && isImgCompatMode()) {
            getImageDataUri(name).then((uri) => { img.src = uri || resolvedPath; });
        } else {
            img.src = resolvedPath;
        }

        wrapper.appendChild(img);
        this._contentEl.appendChild(wrapper);

        // Divider after
        const divAfter = document.createElement('div');
        divAfter.className = 'story-image-divider';
        this._contentEl.appendChild(divAfter);

        this._scrollToBottom();
    }

    _addVideo(name) {
        if (!this._contentEl) return;
        // Mark that this refresh rendered a fresh clip, so the STORY_CHOICES_READY
        // catch-all keeps the active hypno beat audible (see _bindEvents § 91).
        this._videoAddedThisRefresh = true;

        // Remove previous video (new #gif: replaces old, not stacks).
        // Fully release BEFORE detaching (BUG_PATTERNS § 91): a bare pause()
        // left the decode buffer alive on mobile, and if the player had
        // unmuted a hypno clip its audio track could keep looping from the
        // detached node. Isolated clips (iframe) are found via their host
        // frame; _hardReleaseVideo removes the frame (§ 106 teardown).
        const prevClips = [
            ...this._contentEl.querySelectorAll('.story-video'),
            ...this._contentEl.querySelectorAll('iframe.story-video-frame'),
        ];
        for (const pv of prevClips) {
            const pw = pv.closest('.story-image-wrapper');
            const inner = (pv.tagName === 'IFRAME') ? this._frameInnerVideo(pv) : pv;
            if (inner) this._hardReleaseVideo(inner);
            else { try { this._videoVisibilityObserver?.unobserve(pv); pv.remove(); } catch (e) { /* detached */ } }
            // Remove wrapper + surrounding dividers
            if (pw) {
                const prev = pw.previousElementSibling;
                const next = pw.nextElementSibling;
                if (prev && prev.classList.contains('story-image-divider')) prev.remove();
                if (next && next.classList.contains('story-image-divider')) next.remove();
                pw.remove();
            } else if (pv.isConnected) {
                pv.remove();
            }
        }

        // Hypno voice (v0.2.9): the voice rides the <video>'s OWN audio track,
        // routed through a Web Audio GainNode (audioManager.attachHypnoVideo, done
        // below once the <video> exists). Detach the PRIOR beat's routing here so
        // its audio can't survive into this beat. The global 🔊 state
        // (audioManager._hypnoMuted) carries across beats. A reduce-motion / static
        // fallback has no <video>, so it has no voice — acceptable (no motion, no
        // induction loop).
        audioManager.stopHypnoVoice();

        const shot = this._consumePendingShot();

        const divBefore = document.createElement('div');
        divBefore.className = 'story-image-divider';
        this._contentEl.appendChild(divBefore);

        const wrapper = document.createElement('div');
        wrapper.className = `story-image-wrapper shot-${shot}`;

        const videoVariantName = appearanceState.resolveVideoVariantName(name);
        const videoSrc = `assets/videos/${videoVariantName}.mp4`;

        // Cross-media variant pick (owner call 2026-07-23): appearance
        // specificity beats medium. Compare how many appearance suffixes the
        // resolved VIDEO vs the resolved IMAGE carries beyond the base name;
        // show the image when it is STRICTLY closer to the player's state
        // (chastity / longhair / boobs / masc). Ties go to the video (motion
        // is the richer render of the same appearance). Pre-fix this only
        // kicked in when the video fell all the way to base — a partial
        // video variant (_chastity) shadowed a closer still
        // (_chastity_boobs). Same render path as Reduce Motion.
        const _suffixSpecificity = (resolved) =>
            resolved === name ? 0
                : resolved.slice(name.length).split('_').filter(Boolean).length;
        const imgVariantName = appearanceState.resolveVariantName(name);
        const useImageVariant =
            _suffixSpecificity(imgVariantName) > _suffixSpecificity(videoVariantName);

        // Reduce Motion: replace video with static image fallback
        const reduceMotion = localStorage.getItem('PnO_ReduceMotion') === '1';
        if (reduceMotion || useImageVariant) {
            const img = document.createElement('img');
            img.className = 'story-image';
            img.alt = name.replace(/_/g, ' ');
            const imgPath = this._resolveImagePath(name);
            if (imgPath) {
                img.src = imgPath;
                this._setupImageFallback(img, name);
            } else {
                // No image fallback, skip entirely
                return;
            }
            img.addEventListener('click', () => this._expandImage(img.src));
            wrapper.appendChild(img);
            this._contentEl.appendChild(wrapper);

            const divAfter = document.createElement('div');
            divAfter.className = 'story-image-divider';
            this._contentEl.appendChild(divAfter);
            this._scrollToBottom();
            return;
        }

        // Blur placeholder: show blurred static image while video buffers
        const imgFallbackPath = this._resolveImagePath(name);
        if (imgFallbackPath) {
            wrapper.classList.add('video-loading');
            const blurImg = document.createElement('img');
            blurImg.className = 'video-placeholder-blur';
            blurImg.src = imgFallbackPath;
            blurImg.alt = '';
            blurImg.decoding = 'async';
            blurImg.onerror = () => { _warnRenderFail('video-poster', name, blurImg.src); blurImg.remove(); wrapper.classList.remove('video-loading'); };
            wrapper.appendChild(blurImg);
        }

        // ── Isolated playback frame (BUG_PATTERNS § 106, 2026-07-03) ──
        // Chromium permanently retains each inline story clip's decoded-frame
        // pool (media/frame_buffers, ~13MB/clip renderer RSS in software
        // decode, GPU-process memory on hardware decode) when the <video>
        // lives in the main document and is churned through the story flow —
        // the canonical pause+removeAttribute('src')+load() teardown does NOT
        // free it, and the elements are provably GC'd while the pools survive
        // (probe-verified on shipped 0.2.9.1; player report "repeatedly
        // stream → unplayable"). Hosting the clip in a throwaway same-origin
        // <iframe> fixes it: removing the frame destroys its document and
        // with it the WebMediaPlayer + frame pool — verified flat over 15
        // stream sessions where the old path grew +90MB each.
        // Voice clips (HYPNO_VOICE_CLIPS) keep the main-document path: their
        // Web Audio gain routing + §91 stop surfaces depend on it, and a
        // handful of hypno clips per session cannot drive the leak.
        // Kill-switch: localStorage PnO_IsolatedVideo = '0' (inline fallback,
        // also taken automatically if the frame's document is inaccessible).
        this._contentEl.appendChild(wrapper);   // connect NOW — an iframe only gets a document once in the DOM
        let clipFrame = null;
        let video = null;
        let _isolate = !HYPNO_VOICE_CLIPS.has(name);
        try { if (localStorage.getItem('PnO_IsolatedVideo') === '0') _isolate = false; } catch (e) {}
        if (_isolate) {
            try {
                const fr = document.createElement('iframe');
                fr.className = 'story-video-frame';
                fr.setAttribute('scrolling', 'no');
                fr.setAttribute('aria-hidden', 'true');
                fr.setAttribute('tabindex', '-1');
                wrapper.appendChild(fr);
                const idoc = fr.contentDocument;
                idoc.documentElement.style.background = 'transparent';
                idoc.body.style.cssText = 'margin:0;background:transparent;overflow:hidden;';
                video = idoc.createElement('video');   // owned by the frame's document — dies with it
                video.style.cssText = 'width:100%;height:100%;display:block;object-fit:contain;';
                idoc.body.appendChild(video);
                fr._pnoVideo = video;                  // observer callback + release helpers mapping
                clipFrame = fr;
            } catch (e) {
                // Sandboxed / inaccessible frame document (exotic embedder) —
                // legacy inline path still works, it just leaks on Chromium.
                try { wrapper.querySelector('iframe.story-video-frame')?.remove(); } catch (e2) {}
                clipFrame = null;
                video = null;
            }
        }
        if (!video) video = document.createElement('video');
        video.className = 'story-video';
        // Stamp the scene this clip belongs to, so _onAppend can pause it once a
        // DIFFERENT scene renders (the location-based stop that survives the
        // deferred-family-clear path — see _onAppend).
        let _beatLoc = '';
        try { _beatLoc = (storyManager && storyManager.currentLocation) || ''; video.dataset.clipLoc = _beatLoc; } catch (e) {}
        // A DEFERRED play-path (loadeddata, the 1500ms reveal timeout, or the
        // onerror -> load() retry) must NOT restart this clip after the player
        // left its beat. `document.contains(video)` is INSUFFICIENT: hypno lives
        // in one location-family, so scroll-append KEEPS the previous card in
        // the DOM (deferred family-clear) -> the element stays connected while
        // currentLocation moves on. A pending timer firing post-leave then
        // play()s the `loop` element, which reverts to native (un-gained) audio
        // and loops forever ("Ton startet neu und loopt", VPS 0.2.8.6 Firefox).
        // detachHypnoVideo already paused it; this stops a stale timer un-pausing
        // it. Bail when the beat moved away OR the card detached.
        const _beatLeft = () => {
            if (clipFrame ? !clipFrame.isConnected : !document.contains(video)) return true;
            try { return ((storyManager && storyManager.currentLocation) || '') !== _beatLoc; }
            catch (e) { return false; }
        };
        // Voice clips play ONCE then freeze on the last frame (one-shot, matches
        // the one-shot voice — owner decision 2026-06-22); short tonless ambient
        // loops (usb/spiral_loop/sissy_flash) keep looping visually. Replay
        // button restarts video+voice once.
        video.loop = !HYPNO_VOICE_CLIPS.has(name);
        // Stamp audio-bearing clips so the §91 stop-surfaces can tell them from
        // SILENT clips (streaming action gifs, ambient loops). Only audio clips
        // need the hard pause + loop=false to kill a leaking voice; a silent
        // clip's muted loop is harmless and IS the intended scrollback visual.
        if (HYPNO_VOICE_CLIPS.has(name)) video.dataset.hasVoice = '1';
        video.muted = true;            // silent autoplay; the voice is the separate .ogg (audio.js)
        // Hypno-voice clips (v0.2.9): the audio is routed through a Web Audio
        // GainNode (attachHypnoVideo below). The ELEMENT must stay at full volume
        // so the source node receives the full signal once 🔊 unmutes it — the
        // GainNode is the actual volume/mute control. Non-hypno videos keep the
        // SFX-slider volume (harmless — they're muted).
        try { video.volume = HYPNO_VOICE_CLIPS.has(name) ? 1 : audioManager.sfxVolume; } catch (e) { /* keep default */ }
        video.playsInline = true;
        video.setAttribute('playsinline', '');      // iOS Safari <10 compatibility
        video.setAttribute('webkit-playsinline', ''); // even older WebKit
        video.autoplay = true;
        video.preload = 'metadata';
        video.disableRemotePlayback = true;         // hide AirPlay button on iOS (distraction in narrative)
        // Isolated frame: resolve against the PARENT document explicitly —
        // about:blank inherits the creator's base URL, but exotic embedders
        // (file://, custom schemes) are safer with an absolute URL.
        video.src = clipFrame ? new URL(videoSrc, document.baseURI).href : videoSrc;

        // Fallback to image if video fails — but retry once first. Videos
        // stream straight from itch.io's BunnyCDN with no retry path (unlike
        // story.json), and the edge intermittently 403s/drops a request that
        // succeeds on a second hit. Pre-fix the player just saw the still
        // image instead of the intended motion ("only some gifs load",
        // foundby intake 0.2.7.3.1 Android). Re-issue the same bare URL once
        // (force reload past any negative cache) before degrading to the image.
        let _videoRetried = false;
        let _degraded = false;
        // Degrade to the still image — shared by onerror's second failure AND
        // the stall watchdog (a load that never fires `error` but also never
        // reaches HAVE_CURRENT_DATA). Idempotent via _degraded.
        //
        // keepForUpgrade=true (stall-watchdog caller only): the source has NOT
        // errored, it's just slow — a big hypno clip on a weak connection can
        // still arrive after the 10s window. Show the still NOW, but keep the
        // <video> alive + hidden so a frame that finally buffers UPGRADES back
        // to motion, instead of stranding the player on a permanent still
        // ("gifs don't autoplay anymore", foundby:Inciteful 0.2.9.0.2 — the
        // same player was reporting lag). keepForUpgrade=false (onerror's hard
        // 2nd-failure): the source is dead, free the element, no upgrade.
        const _degradeToImage = (keepForUpgrade) => {
            if (_degraded) return;
            _degraded = true;
            const ctrl = wrapper.querySelector('.video-controls');
            const pb = wrapper.querySelector('.video-play-btn');
            if (pb) pb.remove();
            const imgPath = this._resolveImagePath(name);
            if (!imgPath) {
                if (!keepForUpgrade) { if (clipFrame) clipFrame.remove(); else video.remove(); if (ctrl) ctrl.remove(); wrapper.style.display = 'none'; }
                console.warn(`[StoryContent] Video + image both missing: ${name}`);
                return;
            }
            const img = document.createElement('img');
            img.className = 'story-image video-degraded-still';
            img.alt = name.replace(/_/g, ' ');
            img.src = imgPath;
            this._setupImageFallback(img, name);
            img.addEventListener('click', () => this._expandImage(img.src));
            wrapper.appendChild(img);
            if (!keepForUpgrade) {
                // Hard failure — free the clip + controls (old behaviour).
                // Isolated clip: dropping the frame is the § 106 teardown.
                if (clipFrame) clipFrame.remove(); else video.remove();
                if (ctrl) ctrl.remove();
                return;
            }
            // Slow load: hide the clip (keeps loading — display:none does not
            // stop the network fetch) and re-reveal it once a frame arrives.
            // Isolated clip: hide the host FRAME (parent CSS can't reach the
            // frame-internal element).
            (clipFrame || video).style.display = 'none';
            if (ctrl) ctrl.style.display = 'none';
            const _upgrade = () => {
                if (_beatLeft()) return;           // player moved on — keep the still
                if (video.readyState < 2) return;  // still no frame
                (clipFrame || video).style.display = '';
                if (ctrl) ctrl.style.display = '';
                img.remove();
                wrapper.classList.remove('video-loading');
                wrapper.classList.add('video-loaded');
                _tryPlay();
            };
            video.addEventListener('canplay', _upgrade, { once: true });
            video.addEventListener('loadeddata', _upgrade, { once: true });
        };
        video.onerror = () => {
            if (!_videoRetried) {
                _videoRetried = true;
                setTimeout(() => {
                    // Don't reload a clip whose beat the player already left —
                    // load() re-runs resource selection and would re-fire
                    // loadeddata -> _tryPlay -> restart the loop (the user's
                    // "reload triggers it" symptom). See _beatLeft above.
                    if (_beatLeft()) return;
                    // load() re-runs resource selection on the current src —
                    // a fresh network request past the negative-cached 403.
                    try { video.load(); } catch (_) { /* image fallback on next error */ }
                }, 600);
                return;
            }
            _warnRenderFail('video', name, video.currentSrc || video.src);
            _degradeToImage();
        };

        // Reveal video + start playback robustly. The reveal class and play()
        // must NOT both hang off `loadeddata`: on some mobile Chrome configs
        // (Data Saver / battery-saver) preload='metadata' never advances
        // readyState past 1 until play() is called, while suppressed muted-
        // autoplay means play() is never auto-invoked — so `loadeddata` never
        // fires and the opacity:0 element stays a black/blurred box forever
        // (VPS report 0.2.7.6.3 Android: "All video clips are black screens").
        // Fix: kick play() immediately (breaks the circular dependency), reveal
        // on the earlier `loadedmetadata` too, and hard-timeout as last resort.
        let _revealed = false;
        const _reveal = () => {
            if (_revealed) return;
            _revealed = true;
            // Host frame height: match the clip so the wrapper sizes exactly
            // like the legacy inline <video> did (default 1/1 via CSS).
            if (clipFrame && video.videoWidth > 0 && video.videoHeight > 0) {
                clipFrame.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
            }
            wrapper.classList.remove('video-loading');
            wrapper.classList.add('video-loaded');
        };
        const _removePlayBtn = () => {
            const b = wrapper.querySelector('.video-play-btn');
            if (b) b.remove();
        };
        const _tryPlay = () => {
            if (_beatLeft()) return;   // never (re)start a clip whose beat was left (stale timer / scrollback)
            const p = video.play();
            if (p) p.then(() => {
                // Playback recovered (later _tryPlay won after an earlier reject):
                // drop any leftover ▶ overlay, or it sits permanently in the middle
                // of a clip that IS playing. _tryPlay fires up to 3× (immediate /
                // loadeddata / 1500ms timeout); the first call rejects while
                // readyState is 0 and adds the button, a later call succeeds, but
                // pre-fix the button was only ever removed on click. foundby:owner
                // 2026-06-20 ("mitten im Video ist perm ein Play-Knopf").
                _removePlayBtn();
            }).catch(() => {
                _reveal(); // never leave it black if autoplay was blocked
                if (wrapper.querySelector('.video-play-btn')) return;
                const playBtn = document.createElement('button');
                playBtn.className = 'video-play-btn';
                playBtn.innerHTML = '&#9654;';
                playBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    // Only drop the ▶ once playback ACTUALLY starts. Pre-fix the
                    // remove was unconditional: a tap on a stuck-source video
                    // removed the button AND nothing played → stranded until the
                    // player relaunched (foundby:DEVILJOKER 0.2.9 Android). The
                    // 'playing' listener also covers the success path.
                    video.play().then(() => playBtn.remove()).catch(() => {});
                });
                wrapper.style.position = 'relative';
                wrapper.appendChild(playBtn);
            });
        };
        // Catch-all: whenever the element actually begins playing (autoplay that
        // succeeded late, or the player tapped to expand/resume), the ▶ overlay is
        // stale, drop it. Cheap, idempotent, covers engines whose play() promise
        // resolves before the frame is presented.
        video.addEventListener('playing', _removePlayBtn);
        video.addEventListener('loadedmetadata', _reveal, { once: true });
        // GUARD against re-playing a clip whose beat the player already left: a
        // slow-loading hypno clip (e.g. the 4MB hypno_surrender_induction) can
        // fire loadeddata AFTER Continue, and the 1500ms reveal-timeout can fire
        // after a fast Continue — _tryPlay() then play()s the `loop` element,
        // which reverts to native (un-gained) audio and loops forever ("Ton
        // startet neu und loopt", VPS 0.2.8.6 Firefox). _beatLeft covers BOTH
        // the detached case AND same-family scroll-append (card stays in the DOM,
        // currentLocation moved on) which document.contains alone misses.
        video.addEventListener('loadeddata', () => { if (_beatLeft()) return; _reveal(); _tryPlay(); }, { once: true });
        setTimeout(() => { if (!_beatLeft()) { _reveal(); _tryPlay(); } }, 1500);
        // Kick playback now — do not wait for loadeddata (it may never fire on mobile).
        _tryPlay();

        // Stall watchdog (foundby:DEVILJOKER 0.2.9, Android): some loads stall
        // with NO `error` event — readyState never reaches HAVE_CURRENT_DATA(2),
        // so onerror's retry never fires and the ▶ overlay (or blurred box) sits
        // forever; pre-fix the player had to relaunch the game to see the clip.
        // Force one reload at 5s; degrade to the still image at 10s if STILL no
        // frame. Guarded by _beatLeft so a stale timer can't restart a clip the
        // player already left (hypno-loop class). readyState>=2 means a frame
        // loaded (autoplay-blocked-but-ready keeps its ▶), so we don't touch it.
        setTimeout(() => {
            if (_beatLeft() || _degraded) return;
            if (video.readyState >= 2) return;        // a frame is loaded — not stuck
            if (!_videoRetried) {
                _videoRetried = true;
                try { video.load(); } catch (_) { /* degrade on the next check */ }
            }
            setTimeout(() => {
                if (_beatLeft() || _degraded) return;
                if (video.readyState >= 2) return;
                console.warn(`[StoryContent] Video stalled (no frame in 10s), showing image (will upgrade if it loads): ${name}`);
                _degradeToImage(true);   // keep the <video> alive so a late load can swap motion back in
            }, 5000);
        }, 5000);

        // Click to expand video in modal
        video.addEventListener('click', () => {
            this._expandVideo(video.src);
        });

        if (!clipFrame) wrapper.appendChild(video);   // isolated clips already live in the frame's document

        // Hypno clips (v0.2.8.7): the VOICE plays as a separate Howler sound from
        // the extracted .m4a (audioManager.playHypnoVoice), and this <video> stays
        // muted=true ALWAYS. We do NOT route it through createMediaElementSource:
        // in Firefox 152 a captured <video> becomes completely un-stoppable (no
        // element API and not even ctx.close() stops it — it pops to native output
        // and loops forever, foundby:owner 0.2.8.6). Howl.stop() on the m4a is
        // reliably stoppable on every platform incl. Android. stopHypnoVoice() on
        // STORY_STOP_MEDIA (refreshView-top) ends it on every leave.
        if (HYPNO_VOICE_CLIPS.has(name)) {
            // Do NOT (re)start the voice during an EMPTY_PANEL_RECOVERY replay.
            // story.js re-emits TAG_VIDEO to restore a visual card when the panel
            // ends up empty after a transition (forced-event return, scene
            // handover). That replay is meant to be VISUAL-ONLY, but re-running
            // playHypnoVoice restarts the voice from 0 — which is exactly the
            // "ton stopt kurz und loopt dann von vorne" after Continue
            // (foundby:owner FF152). The video visual still re-renders; only the
            // audio restart is suppressed. (The ROOT of the whole loop saga —
            // independent of the audio engine.)
            let replaying = false;
            try { replaying = !!(storyManager && storyManager._narrationReplayInProgress); } catch (e) {}
            if (!replaying) {
                try { audioManager.playHypnoVoice(name); } catch (e) { /* manifest not ready — stays silent */ }
            }
            _hypnoVideoRegistry.add(video);   // (kept for the orphan-nuke safety net)
        }

        // Video controls overlay (Sound + Pause + Skip)
        const controls = document.createElement('div');
        controls.className = 'video-controls';

        // Sound toggle — shown for the audio-bearing hypno clips (v0.2.9). The
        // voice is the <video>'s OWN audio track, routed through a Web Audio
        // GainNode (audioManager.attachHypnoVideo). This button toggles that gain:
        // unmute opens the element (the click is the gesture) + gain=volume;
        // re-mute is gain=0 — NEVER element.muted=true, which is a no-op on a
        // playing Android video (bug 532359). Reliably mute/stoppable on every
        // platform. The mute state is GLOBAL (audioManager._hypnoMuted) and carries
        // across beats.
        const hasVoice = HYPNO_VOICE_CLIPS.has(name);
        const muteBtn = document.createElement('button');
        muteBtn.className = 'video-ctrl-btn video-mute-btn';
        muteBtn.textContent = audioManager.hypnoVoiceMuted ? '🔇' : '🔊';
        muteBtn.style.display = hasVoice ? '' : 'none';
        muteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            audioManager.setHypnoVoiceMuted(!audioManager.hypnoVoiceMuted);
            muteBtn.textContent = audioManager.hypnoVoiceMuted ? '🔇' : '🔊';
        });

        // Replay — restart the clip (video from frame 0 + the one-shot voice) for
        // players who want to watch/hear the hypno session again. The voice is
        // one-shot (no loop, FF152-safe), so this is the intended re-watch path.
        const replayBtn = document.createElement('button');
        replayBtn.className = 'video-ctrl-btn video-replay-btn';
        replayBtn.textContent = '↻';   // ↻
        const _replayLabel = L.get('ui.replay');
        replayBtn.title = _replayLabel !== 'ui.replay' ? _replayLabel : 'Replay';
        replayBtn.style.display = hasVoice ? '' : 'none';
        replayBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            try { video.currentTime = 0; video.loop = false; video.play(); } catch (e2) {}
            if (hasVoice) { try { audioManager.playHypnoVoice(name); } catch (e2) {} }
        });

        const pauseBtn = document.createElement('button');
        pauseBtn.className = 'video-ctrl-btn';
        pauseBtn.textContent = '||';
        pauseBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (video.paused) {
                video.play();
                pauseBtn.textContent = '||';
            } else {
                video.pause();
                pauseBtn.textContent = '\u25B6';
            }
        });

        const skipBtn = document.createElement('button');
        skipBtn.className = 'video-ctrl-btn';
        const _skipLabel = L.get('ui.skip');
        skipBtn.textContent = _skipLabel !== 'ui.skip' ? _skipLabel : 'Skip';
        skipBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._videoVisibilityObserver?.unobserve(video);   // clip is about to be replaced by a still
            video.pause();
            audioManager.stopHypnoVoice();   // detach the gain routing before the <video> goes away
            // Replace video with its current frame as static image
            try {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth || 640;
                canvas.height = video.videoHeight || 360;
                canvas.getContext('2d').drawImage(video, 0, 0);
                const img = document.createElement('img');
                img.className = 'story-image';
                img.src = canvas.toDataURL('image/jpeg', 0.8);
                img.addEventListener('click', () => this._expandImage(img.src));
                if (clipFrame) {
                    // Freeze-frame belongs in the PARENT doc; dropping the frame
                    // tears down the playback document (§ 106).
                    wrapper.appendChild(img);
                    clipFrame.remove();
                } else {
                    video.replaceWith(img);
                }
            } catch(e) {
                if (clipFrame) clipFrame.remove(); else video.remove();
            }
            controls.remove();
        });

        controls.appendChild(muteBtn);
        controls.appendChild(replayBtn);
        controls.appendChild(pauseBtn);
        controls.appendChild(skipBtn);
        wrapper.appendChild(controls);
        wrapper.style.position = 'relative';
        // (wrapper was appended to _contentEl before video creation — the
        // isolated playback frame needs a live browsing context to get a
        // document. Same-task appends never paint in between.)

        // Viewport-pause this silent scrollback loop once it scrolls off-screen
        // (see _initVideoVisibilityObserver). Hypno/voice clips are excluded;
        // their lifecycle is owned by the § 91 surfaces. Resume is a guarded
        // _tryPlay (bails on _beatLeft) gated on `loop`, so a stop-surface-frozen
        // clip (loop=false) is never resurrected by scrolling back to it.
        if (!hasVoice && this._videoVisibilityObserver) {
            video._pnoResumeIfVisible = () => { if (video.loop) _tryPlay(); };
            // Isolated clip: observe the host FRAME (an observer in this
            // document cannot observe the frame-internal element); the
            // callback maps back via fr._pnoVideo.
            try { this._videoVisibilityObserver.observe(clipFrame || video); } catch (e) { /* unsupported */ }
        }

        const divAfter = document.createElement('div');
        divAfter.className = 'story-image-divider';
        this._contentEl.appendChild(divAfter);

        this._scrollToBottom();
    }

    /**
     * Show an inner monologue overlay on top of the most recent story image.
     * Multiple #monologue: tags stack inside the same overlay.
     * @param {string} text - monologue text from the Ink tag
     */
    _addMonologue(text) {
        if (!this._contentEl || !text) return;

        // Find the most recent image/video wrapper to overlay
        const wrappers = this._contentEl.querySelectorAll('.story-image-wrapper');
        const wrapper = wrappers.length > 0 ? wrappers[wrappers.length - 1] : null;
        if (!wrapper) {
            // No image to overlay -- fall back to showing as info text
            this._addInfoText(text);
            return;
        }

        // Check for existing overlay on this wrapper (stack multiple lines)
        let overlay = wrapper.querySelector('.monologue-overlay');
        let textEl;
        if (overlay) {
            textEl = overlay.querySelector('.monologue-text');
            if (textEl) {
                // Append new line to existing monologue
                textEl.innerHTML += '<br>' + this._processText(text);
            }
        } else {
            // Create new overlay
            overlay = document.createElement('div');
            overlay.className = 'monologue-overlay';

            textEl = document.createElement('div');
            textEl.className = 'monologue-text';
            textEl.innerHTML = this._processText(text);

            overlay.appendChild(textEl);
            wrapper.appendChild(overlay);

            // Fade in via rAF to ensure the initial opacity:0 is painted first
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    overlay.classList.add('visible');
                });
            });
        }
    }

    /**
     * Remove all monologue overlays from the story content area.
     * Called on clear / append to clean up before new content.
     */
    _removeMonologues() {
        if (!this._contentEl) return;
        const overlays = this._contentEl.querySelectorAll('.monologue-overlay');
        for (const ol of overlays) {
            ol.remove();
        }
    }

    /**
     * Type text char-by-char into an element. Speed from PnO_TextSpeed setting.
     * 0 = instant, 100 = very slow (~50ms/char).
     */
    _typeText(el, html) {
        // Cancel any running typing
        this._cancelTyping();

        const speed = parseFloat(localStorage.getItem('PnO_TextSpeed') || '0');
        // 0 = instant
        if (speed <= 0.01) {
            el.innerHTML = html;
            this._scrollToBottom();
            this.isTyping = false;
            events.emit(Events.STORY_TYPING_COMPLETE);
            return;
        }

        // Parse HTML to plain text for char-by-char, keep HTML for final
        const tmp = document.createElement('span');
        tmp.innerHTML = html;
        const plainText = tmp.textContent || '';

        if (plainText.length === 0) {
            el.innerHTML = html;
            this._scrollToBottom();
            this.isTyping = false;
            events.emit(Events.STORY_TYPING_COMPLETE);
            return;
        }

        // Delay per char: speed 0.3 = ~15ms, 0.5 = ~25ms, 1.0 = ~50ms
        const msPerChar = speed * 50;
        let charIdx = 0;
        this._skipTyping = false;
        this.isTyping = true;

        el.textContent = '';
        el.classList.add('typing-active');
        this._typingEl = el;
        this._typingHtml = html;

        const tick = () => {
            if (this._skipTyping || charIdx >= plainText.length) {
                el.innerHTML = html;
                el.classList.remove('typing-active', 'hidden');
                this._typingTimer = null;
                this._typingEl = null;
                this._typingHtml = null;
                this.isTyping = false;
                this._scrollToBottom();
                events.emit(Events.STORY_TYPING_COMPLETE);
                return;
            }
            el.textContent += plainText[charIdx];
            charIdx++;
            this._scrollToBottom();
            this._typingTimer = setTimeout(tick, msPerChar);
        };

        this._typingTimer = setTimeout(tick, msPerChar);
    }

    _cancelTyping() {
        if (this._typingTimer) {
            clearTimeout(this._typingTimer);
            this._typingTimer = null;
        }
        // Finalize interrupted element so it shows full text
        if (this._typingEl) {
            this._typingEl.innerHTML = this._typingHtml || '';
            this._typingEl.classList.remove('typing-active', 'hidden');
            this._typingEl = null;
            this._typingHtml = null;
        }
        const wasTyping = this.isTyping;
        this.isTyping = false;
        this._skipTyping = true;
        if (wasTyping) {
            events.emit(Events.STORY_TYPING_COMPLETE);
        }
    }

    /**
     * Player-driven skip: finalize the current typing card AND resolve any
     * pending #break: delay. Used by Space / scroll-wheel-down handlers.
     * @returns {boolean} true if anything was actually skipped.
     */
    skipCurrentBeat() {
        let skipped = false;
        if (this.isTyping) {
            this._cancelTyping();
            skipped = true;
        }
        if (storyManager && storyManager.hasPendingDelay) {
            storyManager.skipPendingDelay();
            skipped = true;
        }
        return skipped;
    }

    /**
     * @returns {boolean} true when focus is in a text input / textarea / editable
     * element so Space should reach the field instead of skipping a card.
     */
    _isInputContext(target) {
        if (!target) return false;
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
        if (target.isContentEditable) return true;
        return false;
    }

    /**
     * @returns {boolean} true when any overlay (phone/settings/shop/pause/etc.)
     * is open. Space MUST NOT skip typing while the player is reading the
     * journal or phone, they may need Space for that overlay's own actions.
     */
    _hasOverlayOpen() {
        const overlays = document.querySelectorAll('.overlay');
        for (const ov of overlays) {
            if (!ov.classList.contains('hidden')) return true;
        }
        // Also bail when an image modal is open (its own Esc/F handler owns Space-like keys).
        const modal = document.getElementById('image-modal');
        if (modal && !modal.classList.contains('hidden')) return true;
        return false;
    }

    /**
     * Bind Space + scroll-wheel-down to skip the current typing card / break
     * delay. Player request (Pablito 2026-05-05): some narration is long,
     * give an explicit "speed-up" affordance instead of waiting through the
     * typing animation + #break: pauses.
     *
     * Guards:
     *  - Only fires inside the active game view (no menus / editor).
     *  - Skipped when any .overlay is open or focus is in an input / editable.
     *  - Wheel: only deltaY > 0 (scroll-down). Wheel-up stays passive so the
     *    player can still scroll back through earlier cards in the
     *    scroll-append flow.
     *  - Both inputs are no-ops when nothing is being typed AND no #break: is
     *    pending, choice/Continue handling stays with choices.js.
     */
    _bindSkipInputs() {
        this._skipKeyHandler = (e) => {
            if (e.key !== ' ') return;
            // Don't steal Space from text fields, overlays, or non-game screens.
            if (this._isInputContext(e.target)) return;
            if (this._hasOverlayOpen()) return;
            const gameView = document.getElementById('screen-game-view');
            if (!gameView || gameView.classList.contains('hidden')) return;
            // If there's nothing to skip, let other Space handlers (choices.js
            // Continue) run on this same keydown.
            if (!this.isTyping && !(storyManager && storyManager.hasPendingDelay)) return;
            e.preventDefault();
            // CRITICAL: stop later document-level Space listeners (choices.js
            // _continueKeyHandler) from also running this turn. Without this,
            // a single Space mid-typing would both finalize the current card
            // AND immediately fire the pending Continue → next beat starts
            // with no time to read what just appeared.
            e.stopImmediatePropagation();
            this.skipCurrentBeat();
        };
        // Capture phase so we run BEFORE choices.js's continue handler regardless
        // of when it was registered, and stopImmediatePropagation actually wins.
        document.addEventListener('keydown', this._skipKeyHandler, true);

        // Scroll-wheel: only react when the wheel event lands inside the
        // story panel. Anywhere else (left/right column, overlay) is normal
        // scroll behaviour.
        const storyScrollEl = document.getElementById('story') || this._storyEl;
        this._skipWheelHandler = (e) => {
            if (!e || typeof e.deltaY !== 'number') return;
            if (e.deltaY <= 0) return; // scroll-up = pass-through (scroll-back through old cards)
            if (this._hasOverlayOpen()) return;
            if (!this.isTyping && !(storyManager && storyManager.hasPendingDelay)) return;
            // Only intercept when the wheel actually originated inside #story.
            const target = e.target;
            if (!(target instanceof Node) || !storyScrollEl || !storyScrollEl.contains(target)) return;
            // Don't preventDefault — the scroll itself is still useful. Just
            // also finalize the typing so the player sees the full card while
            // their scroll lands at the bottom.
            this.skipCurrentBeat();
        };
        if (storyScrollEl) {
            storyScrollEl.addEventListener('wheel', this._skipWheelHandler, { passive: true });
        }
    }

    _showTyping() {
        if (this._typingEl) {
            this._typingEl.classList.remove('hidden');
            this._scrollToBottom();
        }
    }

    _hideTyping() {
        if (this._typingEl) {
            this._typingEl.classList.add('hidden');
        }
    }

    _showEnd() {
        if (!this._contentEl) return;
        const el = document.createElement('div');
        el.className = 'info-card';
        el.innerHTML = '<div class="info-text" style="text-align:center; opacity:0.6;"><em>- End -</em></div>';
        this._contentEl.appendChild(el);
        this._scrollToBottom();
    }

    /**
     * pno-tar-healed (background re-download landed): fill the placeholder
     * cards _addImage parked while the archive was incomplete.
     */
    _onTarHealed() {
        const pending = this._pendingHealImages || [];
        if (!pending.length) return;
        this._pendingHealImages = [];
        let healed = 0;
        for (const { name, wrapper } of pending) {
            if (!wrapper || !wrapper.isConnected) continue;
            const path = this._resolveImagePath(name);
            if (!path) continue; // still missing — drop, next scene re-tags it
            const img = document.createElement('img');
            img.className = 'story-image';
            img.alt = name.replace(/_/g, ' ');
            img.dataset.imageName = name;
            img.decoding = 'async';
            img.addEventListener('load', () => {
                const ph = wrapper.querySelector('.story-image-placeholder');
                if (ph) ph.remove();
                wrapper.classList.remove('loading');
            }, { once: true });
            img.addEventListener('error', () => this._markImageUnavailable(img, name), { once: true });
            img.addEventListener('click', () => this._expandImage(img.src));
            img.src = path;
            wrapper.appendChild(img);
            delete wrapper.dataset.pendingImageName;
            healed++;
        }
        if (healed) console.warn(`[StoryContent] Re-resolved ${healed} placeholder image(s) after tar heal`);
    }

    /**
     * Terminal load failure: swap the animated loading placeholder for a
     * compact dim "image unavailable" card. Pre-fix the spinner animated
     * forever and the broken <img> stayed in the modal-navigation list.
     */
    _markImageUnavailable(img, name) {
        try {
            const wrapper = img.closest('.story-image-wrapper');
            const ph = wrapper && wrapper.querySelector('.story-image-placeholder');
            if (ph) {
                ph.innerHTML = `
                <svg viewBox="0 0 400 120" xmlns="http://www.w3.org/2000/svg">
                    <rect width="400" height="120" fill="#161020"/>
                    <text x="200" y="58" text-anchor="middle" font-family="sans-serif" font-size="12" fill="#6e6386">image unavailable</text>
                    <text x="200" y="80" text-anchor="middle" font-family="monospace" font-size="9" fill="#4a405e">${name}</text>
                </svg>`;
            }
            if (wrapper) {
                wrapper.classList.remove('loading');
                wrapper.classList.add('image-missing');
            }
            if (this._storyImages) {
                const i = this._storyImages.indexOf(img);
                if (i >= 0) this._storyImages.splice(i, 1);
            }
            img.remove();
        } catch (e) { /* non-fatal */ }
    }

    // ------------------------------------------------------------------
    // Private: Image Resolution
    // ------------------------------------------------------------------

    _resolveImagePath(name) {
        // Apply appearance variants (_chastity, _longhair)
        const resolved = appearanceState.resolveVariantName(name);

        // 0. Known-missing — skip entirely
        if (imageMissCache.has(resolved)) return null;

        // Tar mode: get blob URL from loaded tar archive
        if (isTarMode()) {
            const url = getImageUrl(resolved);
            if (url) return url;
            // Try base name without variant suffix
            if (resolved !== name) {
                const baseUrl = getImageUrl(name);
                if (baseUrl) return baseUrl;
            }
            return null;
        }

        // Normal mode: manifest lookup (name → folder), no guessing
        const ext = '.webp';

        // 1. Check in-memory cache (fastest)
        if (imageCache[resolved]) {
            return `assets/images/${imageCache[resolved]}/${resolved}${ext}`;
        }
        if (resolved !== name && imageCache[name]) {
            return `assets/images/${imageCache[name]}/${resolved}${ext}`;
        }

        // 2. Manifest lookup: resolved name first, then base name
        if (imageManifest) {
            const folder = imageManifest[resolved] || imageManifest[name];
            if (folder) {
                imageCache[resolved] = folder;
                _debounceSaveImageCache();
                return `assets/images/${folder}/${resolved}${ext}`;
            }
        }

        // 3. No manifest entry — return null (don't guess folders)
        return null;
    }

    _setupImageFallback(img, name, folderIndex = 0) {
        img.onerror = () => {
            // In tar mode, images are blob URLs. A blob render failure means
            // broken device storage (BUG_PATTERNS 109 diagnostics; Android
            // reports 2026-07-11): retry ONCE through the RAM-only data: URI
            // path before declaring the render dead. Second error = the data
            // URI failed too — device storage is unreadable client-side.
            if (isTarMode()) {
                if (!img.dataset.pnoDataRetry) {
                    img.dataset.pnoDataRetry = '1';
                    _warnRenderFail('scene-img', name, img.src);
                    _noteRenderFail(name);
                    getImageDataUri(name).then((uri) => {
                        if (uri) {
                            console.warn(`[StoryContent] MEDIA_RENDER_RETRY kind=scene-img name=${name} -> data-uri`);
                            img.addEventListener('load', _noteRetryOk, { once: true });
                            img.src = uri;
                        }
                    }).catch(() => { /* diagnostic already logged */ });
                    return;
                }
                _warnRenderFail('scene-img', name, img.src);
                return;
            }

            // Manifest-based: extension fallback chain only (no folder scanning).
            // Use the variant-resolved name (e.g. bath_solo_climax_longhair) so
            // the fallback chain stays inside the same variant. Pre-fix this
            // path used `name` (base) and silently dropped the variant on every
            // .webp → .png fallback — the player saw the no-hair base PNG even
            // when the longhair PNG existed.
            if (imageManifest) {
                const resolved = appearanceState.resolveVariantName(name);
                const folder = imageManifest[resolved] || imageManifest[name];
                if (folder) {
                    const src = img.src;
                    const targetName = imageManifest[resolved] ? resolved : name;
                    if (src.endsWith('.webp')) {
                        img.src = `assets/images/${folder}/${targetName}.png`;
                        return;
                    }
                    if (src.endsWith('.png')) {
                        img.src = `assets/images/${folder}/${targetName}.jpg`;
                        return;
                    }
                }
                imageMissCache.add(name);
                this._markImageUnavailable(img, name);
                return;
            }

            // No manifest: sequential folder scan (legacy fallback)
            const src = img.src;
            if (src.endsWith('.webp')) {
                img.src = src.replace(/\.webp$/, '.png');
                return;
            }
            folderIndex++;
            if (folderIndex < IMAGE_FOLDERS.length) {
                img.src = `assets/images/${IMAGE_FOLDERS[folderIndex]}/${name}.webp`;
                this._setupImageFallback(img, name, folderIndex);
            } else {
                imageMissCache.add(name);
                this._markImageUnavailable(img, name);
            }
        };
        img.onload = () => {
            // Prefer manifest folder as the cache value — IMAGE_FOLDERS is only
            // a sequential-scan fallback when no manifest exists. Pre-fix this
            // line wrote IMAGE_FOLDERS[folderIndex] FIRST (which is "Apartment"
            // at folderIndex=0, the default arg), so any successfully-loaded
            // image whose manifest folder wasn't Apartment got its cache
            // overwritten to "Apartment". On the next load _resolveImagePath
            // returned `assets/images/Apartment/<name>.webp` → 404 → fallback
            // chain. Visible noise in DevTools console; gameplay was correct
            // because the .png fallback eventually re-routed via manifest, but
            // every screenshot session showed dozens of phantom 404s.
            // Foundby:Playwright streaming pass 2026-05-06.
            const resolved = appearanceState.resolveVariantName(name);
            const manifestFolder = imageManifest?.[resolved] || imageManifest?.[name];
            imageCache[name] = manifestFolder || IMAGE_FOLDERS[folderIndex] || '';
            _debounceSaveImageCache();
        };
    }

    // ------------------------------------------------------------------
    // Private: Image Modal
    // ------------------------------------------------------------------

    _bindImageModal() {
        const modal = document.getElementById('image-modal');
        const modalImg = document.getElementById('image-modal-img');
        const modalClose = document.getElementById('image-modal-close');

        if (!modal || !modalImg) return;

        // Image counter element
        const counter = document.createElement('div');
        counter.className = 'image-modal-counter';
        modal.appendChild(counter);
        this._modalCounter = counter;

        const closeModal = () => {
            // Tear down any expanded video (_expandVideo) BEFORE hiding.
            // Hiding the modal does NOT stop a native <video> — an unmuted
            // hypno clip kept playing its audio track in the background
            // (player report 2026-06-05). The X button stopPropagation()s and
            // ESC calls closeModal() directly, so neither reaches the old
            // modal-click cleanup listener. Mirrors the gallery.js
            // remove()-as-stop convention; covers all 3 close paths.
            // MUST be querySelectorAll: a double-click race on the inline
            // video can stack TWO modal videos (newest inserted first via
            // modalImg.after). The 0.2.7.6.2 querySelector version removed
            // only the newest — the older UNMUTED one kept looping behind
            // the hidden modal forever ("Good girl, stay caged" reports,
            // 4× on 2026-06-05, Chrome + Firefox desktop).
            this._teardownModalVideos();
            modal.classList.add('hidden');
            document.body.style.overflow = '';
            if (document.fullscreenElement) {
                document.exitFullscreen().catch(() => {});
            }
        };

        // Close on click anywhere
        modal.addEventListener('click', closeModal);
        if (modalClose) {
            modalClose.addEventListener('click', (e) => {
                e.stopPropagation();
                closeModal();
            });
        }

        // Keyboard: ESC close, arrows navigate, F fullscreen
        // Remove previous listener if exists (prevents accumulation)
        if (this._imageModalKeyHandler) {
            document.removeEventListener('keydown', this._imageModalKeyHandler);
        }
        this._imageModalKeyHandler = (e) => {
            if (modal.classList.contains('hidden')) return;

            if (e.key === 'Escape') {
                closeModal();
                return;
            }

            const imgs = this._storyImages || [];
            if (imgs.length <= 0) return;

            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if (this._modalImageIndex > 0) {
                    this._modalImageIndex--;
                    modalImg.src = imgs[this._modalImageIndex].src;
                    this._updateImageCounter();
                    this._updateVoteState(modalImg.src);
                }
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (this._modalImageIndex < imgs.length - 1) {
                    this._modalImageIndex++;
                    modalImg.src = imgs[this._modalImageIndex].src;
                    this._updateImageCounter();
                    this._updateVoteState(modalImg.src);
                }
            } else if (e.key === 'f' || e.key === 'F') {
                e.preventDefault();
                if (document.fullscreenElement) {
                    document.exitFullscreen().catch(() => {});
                } else {
                    modal.requestFullscreen().catch(() => {});
                }
            }
        };
        document.addEventListener('keydown', this._imageModalKeyHandler);

        // Android/Chrome keeps an unmuted <video>'s media session playing
        // when the tab is backgrounded — pause the expanded modal video on
        // page-hide so hypno audio can't continue behind other apps.
        if (this._modalVisibilityHandler) {
            document.removeEventListener('visibilitychange', this._modalVisibilityHandler);
        }
        this._modalVisibilityHandler = () => {
            if (!document.hidden) return;
            // ALL modal videos, not just the first — see closeModal note.
            modal.querySelectorAll('video.image-modal-img').forEach(v => {
                try { v.pause(); } catch (e) { /* already detached */ }
            });
            // § 106 frame-hosted modal clip: pause the frame-internal video.
            modal.querySelectorAll('iframe.image-modal-img').forEach(fr => {
                try { (fr._pnoVideo || fr.contentDocument?.querySelector('video'))?.pause(); } catch (e) { /* dead frame */ }
            });
        };
        document.addEventListener('visibilitychange', this._modalVisibilityHandler);
    }

    /**
     * Pause + remove EVERY expanded modal video and restore the modal <img>.
     * Single teardown used by closeModal, _expandVideo (pre-clear so a
     * double-click race can't stack two videos), _expandImage and _onClear
     * (location change / save load sweeps stray videos). Pause must come
     * before remove: hiding or detaching alone does not reliably stop an
     * unmuted native <video>'s audio. Returns the number of videos removed.
     */
    _teardownModalVideos() {
        const modal = document.getElementById('image-modal');
        if (!modal) return 0;
        const vids = modal.querySelectorAll('video.image-modal-img');
        for (const v of vids) {
            try { v.pause(); } catch (e) { /* detached mid-flight */ }
            v.remove();
        }
        // § 106: frame-hosted modal clips — removing the frame destroys its
        // document and deterministically frees the player + frame pool.
        const frames = releaseClipsIn(modal, 'iframe.image-modal-img');
        if (vids.length || frames) {
            const modalImg = document.getElementById('image-modal-img');
            if (modalImg) modalImg.style.display = '';
        }
        return vids.length + frames;
    }

    _updateImageCounter() {
        if (!this._modalCounter) return;
        const imgs = this._storyImages || [];
        if (imgs.length <= 1) {
            this._modalCounter.style.display = 'none';
        } else {
            this._modalCounter.style.display = '';
            this._modalCounter.textContent = `${this._modalImageIndex + 1}/${imgs.length}`;
        }
    }

    _updateVoteState(src) {
        const modal = document.getElementById('image-modal');
        if (!modal) return;
        const voteBar = modal.querySelector('.image-vote-bar');
        if (!voteBar) return;
        const imageName = src.split('/').pop().replace(/\.\w+$/, '');
        try {
            const votes = JSON.parse(localStorage.getItem('PnO_ImageVotes') || '{}');
            voteBar.querySelectorAll('.vote-btn').forEach(b => {
                b.classList.toggle('voted', b.dataset.vote === votes[imageName]);
            });
        } catch(e) {}
    }

    _expandVideo(src) {
        const modal = document.getElementById('image-modal');
        const modalImg = document.getElementById('image-modal-img');
        if (!modal || !modalImg) return;

        // Pre-clear: a rapid double-click on the inline video can fire this
        // twice before the modal paints. Without the sweep the second video
        // stacks on the first and closeModal's teardown used to miss the
        // older (unmuted) one — endless background audio loop.
        this._teardownModalVideos();

        // § 91 seventh surface (foundby:VPS 0.2.8.1, "starts a new track
        // overlapping the original... nonsensical static, quite loud"): the
        // modal is about to own the screen, but the INLINE source clip keeps
        // playing UNDER it. If the player unmuted the inline hypno video via
        // the 🔊 button first, expanding it leaves the inline voice looping
        // behind the modal while the modal plays a SECOND copy from t=0 — two
        // out-of-phase loops = the static the report describes. Mute every
        // inline clip so only the modal copy can be audible. Mute (not pause):
        // the inline keeps its silent visual loop, so closing the modal returns
        // to a live spiral, and it stays muted afterwards (no audio zombie).
        if (this._contentEl) {
            this._contentEl.querySelectorAll('video.story-video').forEach(v => {
                try { v.muted = true; } catch (e) { /* detached mid-flight */ }
            });
        }

        // Replace img with video temporarily. § 106: silent clips play inside
        // a throwaway playback frame (voice clips stay inline — their §91
        // audio surfaces sweep main-document videos). Side effect of the
        // frame: clicks on the clip/controls no longer bubble to the modal's
        // click-anywhere-closes handler — native controls become actually
        // usable; backdrop / X / ESC still close.
        const { host: _modalHost, video, frame: _modalFrame } = createIsolatedClip(
            { hostClassName: 'image-modal-img video-fade-in', forceInline: isVoiceClip(src) },
            (h) => { modalImg.after(h); },
        );
        video.src = _modalFrame ? new URL(src, document.baseURI).href : src;
        video.loop = true;
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;            // iOS/Safari block non-muted autoplay; user can unmute via controls
        video.setAttribute('playsinline', '');  // older Safari needs the attr form too
        video.controls = true;
        // Respect the game's SFX volume slider when the player unmutes —
        // a native <video> is outside Howler, so without this the hypno
        // voice track played at 100% regardless of settings (player report
        // 2026-06-05 "the audio does not respect the settings").
        try { video.volume = audioManager.sfxVolume; } catch (e) { /* keep default */ }
        video.preload = 'metadata';
        // Robust reveal: on mobile, `loadeddata` may never fire (metadata
        // preload + suppressed autoplay), leaving an opacity:0 box over the
        // dark modal bg = black screen. Reveal on loadedmetadata + a timeout,
        // and kick play() so it isn't waiting on a reveal that waits on it.
        const _revealModal = () => _modalHost.classList.add('is-loaded');
        video.addEventListener('loadedmetadata', _revealModal, { once: true });
        video.addEventListener('loadeddata', _revealModal, { once: true });
        setTimeout(() => { if (_modalHost.isConnected) _revealModal(); }, 1500);
        try { const _p = video.play(); if (_p) _p.catch(() => {}); } catch (e) { /* controls fallback */ }
        modalImg.style.display = 'none';
        // (host already inserted after modalImg by createIsolatedClip)

        modal.classList.remove('hidden');
        // Teardown happens in _bindImageModal's closeModal() — the single
        // funnel for backdrop click, X button and ESC. (A {once:true}
        // modal-click cleanup here missed the X/ESC paths and leaked the
        // unmuted audio; removed 2026-06-05.)
    }

    _expandImage(src) {
        const modal = document.getElementById('image-modal');
        const modalImg = document.getElementById('image-modal-img');
        if (!modal || !modalImg) return;

        // Remove any leftover videos from _expandVideo (all of them — a
        // double-click race can stack more than one)
        this._teardownModalVideos();

        // § 91 seventh surface (sibling of _expandVideo): a fullscreen image
        // takes over the screen, so an unmuted inline hypno clip must not keep
        // looping its voice behind it. Mute every inline clip (same rule as
        // _expandVideo — image modal owns the screen, only it may be audible).
        if (this._contentEl) {
            this._contentEl.querySelectorAll('video.story-video').forEach(v => {
                try { v.muted = true; } catch (e) { /* detached mid-flight */ }
            });
        }

        modalImg.src = src;
        modal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';

        // Track current image index for arrow-key navigation
        const imgs = this._storyImages || [];
        this._modalImageIndex = imgs.findIndex(i => i.src === src);
        this._updateImageCounter();

        // Add vote buttons if consent given
        let voteBar = modal.querySelector('.image-vote-bar');
        if (!voteBar) {
            voteBar = document.createElement('div');
            voteBar.className = 'image-vote-bar';
            const _vt = (k, fb) => { const v = L.get(k); return v !== k ? v : fb; };
            const loveTitle = _vt('ui.vote_love', 'Love it');
            const skipTitle = _vt('ui.vote_skip', 'Needs work');
            voteBar.innerHTML = `
                <button class="vote-btn vote-up" data-vote="up" title="${loveTitle}">&#x2764;</button>
                <button class="vote-btn vote-down" data-vote="down" title="${skipTitle}">&#x1F44E;</button>
            `;
            voteBar.addEventListener('click', (e) => {
                const btn = e.target.closest('.vote-btn');
                if (!btn) return;
                const vote = btn.dataset.vote;
                const imageName = this._imageNameForSrc(src);
                if (!imageName) { e.stopPropagation(); return; }
                // Queue vote for batched Discord send
                imageVoteService.recordVote(imageName, vote === 'up');
                // Store locally
                try {
                    const votes = JSON.parse(localStorage.getItem('PnO_ImageVotes') || '{}');
                    votes[imageName] = vote;
                    localStorage.setItem('PnO_ImageVotes', JSON.stringify(votes));
                } catch(e) {}
                // Visual feedback
                voteBar.querySelectorAll('.vote-btn').forEach(b => b.classList.remove('voted'));
                btn.classList.add('voted');
                e.stopPropagation();
            });
            modal.appendChild(voteBar);
        }
        // Reset vote state
        const imageName = this._imageNameForSrc(src);
        try {
            const votes = JSON.parse(localStorage.getItem('PnO_ImageVotes') || '{}');
            voteBar.querySelectorAll('.vote-btn').forEach(b => {
                b.classList.toggle('voted', imageName != null && b.dataset.vote === votes[imageName]);
            });
        } catch(e) {}
    }

    // Resolve the stable asset name for a displayed image src. Tar-loaded
    // images carry a `blob:.../<uuid>` src, so parsing the src directly records
    // a random per-session UUID (the historical vote-attribution bug — see
    // image_votes blob-UUID rows). The real name is stamped on the <img> at
    // render time as data-image-name; look it up via the tracked element.
    _imageNameForSrc(src) {
        const imgs = this._storyImages || [];
        const el = imgs.find(i => i.src === src);
        if (el && el.dataset && el.dataset.imageName) return el.dataset.imageName;
        // Fallback for untracked real-path images only. Never trust a blob: or
        // data: URL here — blob's final segment is a per-session UUID and data:
        // ends in a base64 tail (e.g. "9k="), both of which poison the vote DB.
        if (typeof src === 'string' && !src.startsWith('blob:') && !src.startsWith('data:')) {
            return src.split('/').pop().replace(/\.\w+$/, '');
        }
        return null;
    }

    // ------------------------------------------------------------------
    // Private: Scroll
    // ------------------------------------------------------------------

    _scrollToBottom() {
        if (!this._storyEl) return;
        // Player opt-in: Settings > Auto Scroll. Default OFF, only fires when
        // the player explicitly enables it. Reader-paced players miss content
        // above the fold when the panel jumps. Foundby:Kiran v0.2.5.8.1
        // ("flips to bottom of page automatically, then need to scroll back to
        // the top to see scene/pics you missed").
        try {
            if (localStorage.getItem('PnO_AutoScroll') !== '1') return;
        } catch(e) {}
        requestAnimationFrame(() => {
            if (!this._scrollAnchor) return;
            const el = this._storyEl;
            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
            if (!nearBottom) return;
            // Manual scroll on the inner container only. scrollIntoView walks
            // every scrollable ancestor and yanks the page when #story is
            // embedded in a scrollable host (F95 iframe, mobile viewports
            // where body overflows). Compute the anchor's position relative
            // to the container and scrollTo on the container directly so the
            // page scroll never moves. Foundby:Boodstain v0.2.5.7.2.
            const target = el.scrollTop
                + this._scrollAnchor.getBoundingClientRect().top
                - el.getBoundingClientRect().top;
            el.scrollTo({ top: target, behavior: 'smooth' });
        });
    }

    _bindScrollIndicator() {
        const indicator = document.getElementById('scroll-indicator');
        if (!indicator || !this._storyEl) return;

        // Remove a previously-bound handler so re-init doesn't accumulate listeners.
        if (this._scrollIndicatorHandler && this._scrollIndicatorEl) {
            this._scrollIndicatorEl.removeEventListener('scroll', this._scrollIndicatorHandler);
        }
        this._scrollIndicatorHandler = () => {
            const el = this._storyEl;
            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
            indicator.classList.toggle('hidden', nearBottom);
        };
        this._scrollIndicatorEl = this._storyEl;
        this._storyEl.addEventListener('scroll', this._scrollIndicatorHandler);
    }

    // ------------------------------------------------------------------
    // Private: Text Processing
    // ------------------------------------------------------------------

    /**
     * Process Ink text: handle bold, italic, line breaks.
     * @param {string} text
     * @returns {string} HTML
     */
    _processText(text) {
        // Ink uses ** for bold, * for italic (markdown-like)
        let html = text;
        // Bold
        html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        // Italic
        html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
        // Line breaks
        html = html.replace(/\n/g, '<br>');
        return html;
    }
}

export const storyContent = new StoryContent();

/** Expose image manifest and cache for other modules (e.g. gallery). */
export function getImageManifest() { return imageManifest; }
export function getImageCache() { return imageCache; }
export function getImageMissCache() { return imageMissCache; }

if (typeof window !== 'undefined') {
    // analytics.js reads via window — importing story-content there would
    // create an import cycle (story-content already imports analytics).
    window.__imageMissNames = () => Array.from(imageMissCache);
}
